export default {
  generationFailureDialog: {
    title: "Generation failed",
    message: "AI service is temporarily unavailable. Please try again later.",
    fallbackDetail:
      "This may be caused by network instability or a busy server.",
    refundText: "Points refunded",
    retryText: "Generate again",
    backText: "Back",
  },
  dashboard: {
    title: "Workbench",
    welcome: "Welcome back, {name} · {date}",
    creator: "Creator",
    metricsLabel: "Workbench overview",
    pointsBalance: "Points Used (Last 7 Days)",
    workflowTotal: "Workflow Video Duration (Last 7 Days)",
    canvasTotal: "Canvas Video Duration (Last 7 Days)",
    scriptTotal: "Script Episodes (Last 7 Days)",
    pointsUnit: "points",
    secondsUnit: "sec",
    episodesUnit: "eps",
    changeRate: "vs. prior period {value}%",
    noChange: "vs. prior period --",
    recentWorkflow: "Recent Comic Workflow Projects",
    recentCanvas: "Recent Canvas Projects",
    recentScripts: "Recent Scripts",
    workflowProjects: "Comic Projects",
    canvasProjects: "Canvas Projects",
    trendTitle: "Points Usage Trend in the Last 30 Days",
    trendAria: "Comic and canvas projects created in the last 30 days",
    viewAll: "View all",
    view: "View",
    download: "Download",
    downloadFailed: "Script download failed. Please try again later.",
    emptyProjects: "No projects yet",
    emptyScripts: "No scripts yet",
    untitled: "Untitled",
    script: "Script",
    episodes: "{count} episodes",
    styleUnset: "Style not set",
    recently: "Recently updated",
    today: "Today",
    yesterday: "Yesterday",
    daysAgo: "{count} days ago",
  },
  common: {
    language: "Language",
    loading: "Loading...",
    uploading: "Uploading...",
    search: "Search",
    cancel: "Cancel",
    confirm: "Confirm",
    tip: "Notice",
    saveChanges: "Save changes",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    select: "Select",
    clickSelect: "Click to select",
    selectAll: "Select all",
    batchGenerate: "Batch Generate",
    generating: "Generating",
  },
  confirmDialog: {
    logout: {
      title: "Sign out?",
      message:
        "Signing out will not delete any data. You can sign in to this account again at any time.",
      confirm: "Sign Out",
    },
  },
  teamManagementPage: {
    title: "Team Management",
    currentTeam: "Current team:",
    people: "members",
    seats: "Seats",
    createAccount: "Create Account",
    inviteMember: "Invite Member",
    invite: {
      title: "Generate Invite Link",
      generatedTitle: "Invite Link Generated",
      close: "Close invite member dialog",
      link: "Invite Link",
      generateLink: "Generate Invite Link",
      copyLink: "Copy",
      resultTip: "Send this link to a member. They will join the team after registering.",
      copySuccess: "Invite link copied",
      copyFailed: "Failed to copy invite link",
      generateFailed: "Failed to generate invite link",
    },
    subscriptionTip:
      "Subscription points expire at 23:59:59 on each monthly anniversary and do not roll over.",
    searchPlaceholder: "Search by member name or phone number...",
    cards: {
      totalPoints: "Allocatable points total",
      rechargePoints: "Allocatable purchased points",
      subscriptionPoints: "Allocatable subscription points",
      totalConsumed: "Total Points Used",
      rechargeConsumed: "Purchased Points Used",
      subscriptionConsumed: "Subscription Points Used",
    },
    columns: {
      nickname: "Nickname",
      accountLevel: "Account Level",
      role: "Role",
      parentAdmin: "Parent Admin",
      phone: "Phone Number",
      rechargeRemaining: "Purchased Remaining",
      rechargeUsed: "Purchased Used",
      subscriptionRemaining: "Subscription Remaining",
      subscriptionUsed: "Subscription Used",
      status: "Account Status",
      createdAt: "Created At",
      lastLogin: "Last Login",
      actions: "Actions",
    },
    disabled: "Disabled",
    enabled: "Enabled",
    allocatePoints: "Allocate Points",
    resetPassword: "Reset Password",
    remove: "Remove",
    totalPeople: "{count} members",
    editMember: "Edit Member",
    badges: {
      defaultAdmin: "Default Admin",
      defaultAdminTip:
        "The default admin cannot log in; the super admin holds management permissions.",
    },
    accountTypes: {
      superAdmin: "Super Admin",
      levelOneAdmin: "Admin",
      levelTwoAdmin: "Sub Admin",
      admin: "Admin",
      member: "Member",
    },
    form: {
      nickname: "Nickname",
      nicknamePlaceholder: "Member nickname",
      username: "Username",
      usernamePlaceholder: "Enter a username",
      accountType: "Account Type",
      accountTypePlaceholder: "Select an account type",
      role: "Business Role",
      rolePlaceholder: "Select a business role",
      parentAdmin: "Parent Admin",
      parentAdminPlaceholder: "Select a parent admin",
      phone: "Phone Number",
      phonePlaceholder: "11-digit phone number",
      password: "Set Password",
      confirmPassword: "Confirm Password",
    },
    roleHelp: {
      ariaLabel: "View role permission details",
      producer: {
        title: "Producer",
        optionDescription:
          "Highest permissions. Can create and assign projects.",
        description:
          "Has the highest team permissions. Can create and assign projects and assets, and manage all content within projects.",
        scope: "Can view projects they created or were assigned to.",
      },
      director: {
        title: "Director",
        optionDescription:
          "Cannot create projects. Collaborates after being assigned.",
        description:
          "Cannot create projects. Must be assigned to a project before collaborating, and can edit its scripts, storyboards, and assets.",
        scope: "Can only view projects assigned to them.",
      },
      operator: {
        title: "Operator",
        optionDescription:
          "Cannot create projects. Permissions are assigned by an administrator.",
        description:
          "Cannot create projects. Must be assigned to a project before collaborating; available actions depend on permissions granted by an administrator.",
        scope: "Can only view projects assigned to them.",
      },
      adminNote:
        "Data scope follows account level: super admins see the whole enterprise; level-1 admins see their branch; level-2 admins see themselves and direct members. Explicit project grants apply only to the grantee. Infinite Canvas projects are not subject to this restriction and can be created by every role.",
    },
    unbound: "Not Bound",
    verified: "Verified",
    initialPoints: "Initial Points Allocation (Optional)",
    rechargePoints: "Purchased Points",
    subscriptionPoints: "Subscription Points",
    pointsUnit: "points",
    rechargePoolRemaining: "Your allocatable purchased points",
    subscriptionPoolRemaining: "Your allocatable subscription points",
    pointsReadonly: "Points Information (Read-only)",
    removeMember: "Remove Member",
    pointsDialog: {
      pointType: "Point Type",
      poolOption: "{type} (Your remaining: {points})",
      addPoints: "Add Points",
      addDescription:
        "Transfer from your account to this account (your remaining: {points})",
      reclaimPoints: "Reclaim Points",
      reclaimDescription:
        "Reclaim from this account to yours (their remaining: {points})",
      amountPlaceholder: "Enter the number of points",
      confirmAllocate: "Confirm Allocation",
      add: "Add",
      reclaim: "Reclaim",
      amountLabel: "{action} {type} Amount",
      teamPoolRemaining: "Your {type} remaining: ",
      memberRemaining: "This account {type} remaining: ",
    },
    validation: {
      phoneRequired: "Enter a phone number",
      phoneInvalid: "Enter a valid phone number",
      phoneLength: "Enter an 11-digit phone number",
      pointsRequired: "Enter the number of points",
      pointsExceeded: "The amount exceeds the available allocation",
      roleRequired: "Select a business role",
      accountTypeRequired: "Select an account type",
      parentAdminRequired: "Select a parent admin",
      nicknameRequired: "Enter a nickname",
      pointTypeRequired: "Select a point type",
      operationRequired: "Select an operation",
      passwordRequired: "Password is required",
      passwordMin: "Password must be at least 6 characters",
      confirmPasswordRequired: "Confirm your password",
      passwordMismatch: "The two passwords do not match",
      usernameRequired: "Enter a username",
    },
    time: {
      justNow: "Just now",
      minutesAgo: "{n} minutes ago",
      hoursAgo: "{n} hours ago",
      daysAgo: "{n} days ago",
    },
    memberFallback: "M",
    roles: {
      owner: "Owner",
      producer: "Producer",
      director: "Director",
      operator: "Operator",
    },
    messages: {
      success: "Operation successful",
      failed: "Operation failed",
      statusUpdated: "Account status updated",
      statusUpdateFailed: "Failed to update the account status",
      overviewFailed: "Failed to load the team overview",
      membersFailed: "Failed to load the member list",
      removeSuccess: "Member removed successfully",
      removeFailed: "Failed to remove the member",
      resetPasswordFailed: "Failed to reset the password",
      hasChildren: "This account still has direct reports. Reassign them before deleting.",
    },
    removeConfirm: {
      title: "Remove “{name}”?",
      message:
        "This account will be deleted. Remaining purchased and subscription points will transfer to the direct parent. Accounts with direct reports cannot be deleted.",
      confirm: "Remove",
    },
    statusConfirm: {
      enableTitle: "Enable “{name}”?",
      enableMessage:
        "After enabling, this account can sign in and use the product again. Continue?",
      enableConfirm: "Enable",
      disableTitle: "Disable “{name}”?",
      disableMessage:
        "After disabling, this account will be unable to sign in or use the product. Continue?",
      disableConfirm: "Disable",
    },
    resetPasswordConfirm: {
      title: "Reset the password for “{name}”?",
      message:
        "A new password will be generated and the current password will stop working immediately. Continue?",
      confirm: "Reset",
    },
    resetPasswordResult: {
      title: "Reset Password",
      target: "Account",
      newPassword: "New Password",
      tip: "Please share the new password with the user promptly, and remind them to change it after signing in.",
      close: "Close",
      copy: "Copy",
      copySuccess: "Password copied",
      copyFailed: "Failed to copy the password",
    },
  },
  app: {
    title: "AI NINJA AI Short Drama Creation Platform",
    firstLogin: {
      title: "Points Allocation",
      welcome: "Welcome to the enterprise edition.",
      gift: "Gift points have been added to the super admin account. Go to Team Management and {action}.",
      action: "allocate them before use",
      followUp:
        "Later purchases credit the corresponding account. Admins must allocate from their own balance to team members before those points can be used.",
      allocate: "Allocate points",
      dismiss: "Not now",
    },
  },
  activityBanner: {
    ariaLabel: "Limited-time membership offer",
    remaining: "Ends in",
    days: "d",
    hours: "h",
    minutes: "m",
    seconds: "s",
    longTerm: "Ongoing",
    title: "Seedance 2.5 720P annual plan offer",
    description: "Generate from CNY 0.39/sec, annual plans up to 55% off",
    action: "View offer",
    close: "Close promotion banner",
  },
  header: {
    logoAlt: "AI comic drama creation platform",
    taskCenter: {
      title: "Task Center",
      open: "Open task center",
      close: "Close task center",
      typeLabel: "Task type",
      taskTitle: "{type} generation #{index}",
      points: "-{points} points",
      sourceDescription: "Comic / Canvas / Script - Project - Episode - Shot...",
      failureReason: "Failure reason",
      timeoutReason: "Generation service timed out",
      reportError: "One-click feedback",
      report: {
        title: "Report Task Error",
        subtitle: "{task} · {points} points used",
        close: "Close report task error dialog",
        summary: "Error summary (read-only)",
        description: "Describe the problem in one sentence",
        placeholder: "For example: Generation stopped at 80% with no result",
        descriptionRequired: "Please describe the problem",
        cancel: "Cancel",
        submit: "Submit Error",
      },
      tasks: {
        scriptAnalysis: "Script Analysis",
        characterAsset: "Character Asset Generation",
        sceneAsset: "Scene Asset Generation",
        styleTransfer: "Style Transfer",
        coverImage: "Cover Generation",
        storyboard: "Storyboard Generation",
      },
      scrollForMore: "Scroll down to load more",
      allLoaded: "All tasks loaded",
      empty: "No tasks",
      loadFailed: "Failed to load tasks. Please try again later.",
      status: { all: "All", running: "Running", success: "Completed", failed: "Failed" },
      type: { image: "Image Tasks", video: "Video Tasks" },
      description: {
        running: "Generating · {time}",
        success: "Completed · {time}",
        failed: "Failed · {time}",
      },
    },
    nav: {
      home: "Home",
      workflow: "Comic Workflow",
      canvas: "Canvas",
      scriptCreation: "Script Creation",
      dataInsights: "Data Insights",
      efficiencyAnalysis: "Efficiency",
      help: "Help",
      guide: "User Guide",
      productUpdates: "Product Updates",
    },
    language: {
      label: "Switch language",
      zhCN: "简体中文",
      enUS: "English",
      yueHK: "Cantonese",
      shortZhCN: "中文",
      shortEnUS: "EN",
      shortYueHK: "粵語",
    },
    accountMenu: {
      currentAccount: "Current account",
      personalCenter: "Account Center",
      changePassword: "Change Password",
      teamManagement: "Team Management",
      invoice: "Order Management",
      rechargeRecords: "Recharge History",
      filingInfo: "Legal Information",
      feedback: "Feedback",
      contactUs: "Contact Us",
      changelog: "Changelog",
      language: "Language",
      logout: "Sign Out",
    },
    passwordDialog: {
      title: "Reset Password",
      oldPassword: "Current Password",
      newPassword: "New Password",
      confirmNewPassword: "Confirm New Password",
      enterOldPassword: "Enter your current password",
      enterNewPassword: "Enter a new password",
      enterConfirmPassword: "Enter the new password again",
      passwordMismatch: "The passwords do not match.",
      success: "Password changed successfully",
    },
    contactDialog: {
      title: "Contact Support",
      description:
        "For help with the platform, recharge or subscription questions, or feedback, scan the QR code to add our support team on WeChat. We will get back to you as soon as possible.",
      qrCodeAlt: "Customer support WeChat QR code",
    },
    feedbackDialog: {
      bugHint: "Unexpected behavior, failed generation, page errors, or incorrect results.",
      optimizationHint: "The feature works, but its experience, efficiency, presentation, or workflow could be smoother.",
      featureHint: "A capability that is not available yet and should be added or extended.",
      improvementLabel: "How should it improve",
      improvementPlaceholder: "Tell us which steps to reduce or how the interface or workflow should change",
      impactLabel: "Usage frequency / impact",
      impactPlaceholder: "For example: used before every export and more noticeable during collaboration",
      scenarioLabel: "Usage scenario",
      scenarioPlaceholder: "Describe the business scenario that needs this capability",
      goalLabel: "Goal",
      goalPlaceholder: "Describe what should be added and the result you want to achieve",
      valueLabel: "Expected value",
      valuePlaceholder: "For example: increase output, reduce rework, or support a new delivery flow",
      title: "Feedback",
      placeholder: "Feedback",
      workspace: "Workspace",
      close: "Close",
      submitTitle: "Submit feedback",
      submitDesc: "Bugs, improvements and feature requests",
      submit: "Submit feedback",
      saveDraft: "Save draft",
      draftSaved: "Draft saved",
      draftSaveFailed: "Failed to save the draft. Please try again later.",
      draftEmpty: "Fill in at least one field before saving the draft.",
      draftTitle: "{type} feedback: {title}",
      draftStatus: "Draft",
      continueEdit: "Continue editing",
      deleteDraft: "Delete draft",
      submitting: "Submitting...",
      submitSuccess: "Feedback submitted",
      submitFailed: "Failed to submit feedback. Please try again later.",
      uploadFailed: "Failed to upload the attachment. Please try again.",
      uploading: "Uploading...",
      uploaded: "Uploaded",
      removeFile: "Remove file",
      fileCountInvalid: "You can upload up to 5 attachments.",
      fileTypeInvalid: "Only PNG, JPG, JPEG, or MP4 files are supported.",
      fileSizeInvalid: "File size must be greater than 0 and no more than 200 MB.",
      myFeedback: "My feedback",
      empty: "No data",
      listHint: "Review replies, status changes and notifications for your submissions.",
      view: "View",
      platformReply: "Platform reply",
      delete: "Delete",
      formTitle: "Feedback details",
      formHint: "Choose a type and add the necessary details. It will become a support ticket.",
      typeLabel: "Issue type",
      typeBug: "BUG",
      typeOptimization: "Improvement",
      typeFeature: "Feature request",
      titleLabel: "Title",
      titlePlaceholder: "Summarize the issue in one sentence",
      requiredField: "Please enter {field}",
      moduleLabel: "Module",
      moduleCanvas: "Canvas",
      moduleWorkflow: "Workflow",
      moduleAccount: "Account",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Tell us what happened and where it occurred",
      stepsLabel: "Steps to reproduce",
      stepsPlaceholder: "For example: open a project, select a shot, generate, then check the result",
      upload: "Upload screenshot / recording",
      uploadHint: "PNG, JPG and MP4 are supported.",
      detailTitle: "Feedback details",
      readonly: "Read-only information",
      ticket: "Ticket",
      notProvided: "Not provided",
      deleteTitle: "Delete ticket",
      deleteMessage: "Delete this ticket?",
      cancel: "Cancel",
      confirmDelete: "Delete",
      deleteSuccess: "Feedback deleted",
      deleteFailed: "Failed to delete feedback. Please try again later.",
      replyTitle: "Platform reply",
      replyContent: "Reply",
      attachments: "Attachments",
      attachmentCount: "{count} files",
      download: "Download",
      gotIt: "Got it",
      statusProcessing: "Processing",
      statusResolved: "Resolved",
      justNow: "Just now",
    },
    // Unread announcement dialog after successful login
    announcementDialog: {
      badge: "Update",
      fallbackTitle: "Product Update",
      publishedAt: "Published {time}",
      confirm: "Got it",
      closeAria: "Close update notice",
    },
    // Full-screen changelog dialog (paginated announcements API)
    changelog: {
      title: "Changelog",
      close: "Close",
      empty: "No updates yet",
      emptyTip: "Release notes will appear here when a new version is published",
      loading: "Loading...",
      loadFailed: "Failed to load changelog. Please try again later.",
      retry: "Retry",
      fallbackTitle: "Product Update",
    },
    pointsPopover: {
      managePoints: "Points Management",
      currentPoints: "Current Points",
      teamTotalPoints: "Allocatable Points",
      allocatePoints: "Allocate",
      rechargePoints: "Purchased Points",
      subscriptionPoints: "Subscription Points",
      buyMembership: "Membership Subscription",
      buyPoints: "Recharge Points",
      viewUsage: "View Usage",
    },
    memberCenter: {
      editProfile: "Edit Profile",
      editProfileHint: "Only your name can be changed",
      closeEditor: "Close profile editor",
      name: "Name",
      namePlaceholder: "Enter your name",
      phone: "Phone Number",
      accountLevel: "Account Level",
      role: "Role",
      parentAdmin: "Parent Admin",
      monthlyPoints: "Monthly points",
      monthlyPointsEnterpriseHint: "(super admin account)",
      showPhone: "Show",
      hidePhone: "Hide",
      readonlyHint: "The account and permission information above cannot be changed",
      personalAccount: "Personal Account",
      enterpriseMainAccount: "Enterprise Main Account",
      enterpriseSubAccount: "Enterprise Sub-account",
      saveSuccess: "Profile updated",
      addSeats: "Add Seats",
      addSeatsHint:
        "Adding seats increases the available member slots on your current team subscription. New seats take effect immediately and keep the same expiration date as the original team subscription.",
      addSeatsPointsNotice:
        "Points issued for additional seats this month can be used later, and any unused points will reset on {date}.",
      addSeatsPointsHelp:
        "Example\n\n" +
        "Suppose your plan grants 20,000 points per month. You add one seat on August 10, and the system immediately grants 5,000 additional points.\n\n" +
        "Scenario 1: Points remain at the end of the cycle\nOn September 10, the account has 6,000 points remaining\n→ The new cycle receives 20,000 + 5,000 = 25,000 points\n→ On October 10, the balance resets to 20,000 points and the additional-seat points are no longer deferred\n\n" +
        "Scenario 2: Only some points remain\nOn September 10, the account has 3,000 points remaining\n→ The deferred amount is limited to the remaining 3,000 points, so the new cycle receives 23,000 points\n\n" +
        "Scenario 3: All points have been used\nOn September 10, the account balance is 0\n→ No points are deferred, so the new cycle receives 20,000 points",
      addSeatsPointsHelpAria: "View additional-seat points example",
      subscriptionPointsResetNote: "Resets at 23:59:59 on {date} · Used first",
    },
    recharge: {
      bonusRate: "(includes {rate}% bonus)",
    },
    accountCenter: {
      productName: "AI Video Creation",
      nav: {
        account: "Account",
        usage: "Usage",
        rechargeRecords: "Recharge History",
      },
      accountInfo: "Account Information",
      registeredAt: "Registered At",
      account: "Account",
      points: "Points",
      totalPoints: "Points",
      serialNumber: "No.",
      columns: {
        details: "Details",
        time: "Time",
        pointsChange: "Points Change",
        model: "Model",
        resolution: "Resolution",
        duration: "Duration",
        orderNo: "Order No.",
        orderAmount: "Order Amount",
        orderPoints: "Order Points",
        bonusPoints: "Bonus Points",
        paymentMethod: "Payment Method",
        orderTime: "Order Time",
      },
    },
    messages: {
      comingSoon: "Coming soon. Stay tuned.",
    },
  },
  landing: {
    navLabel: "Homepage navigation",
    home: "Home",
    productServices: "Products",
    aboutUs: "About Us",
    login: "Sign In",
    startCreating: "Start Creating",
    authPanel: {
      browserUnsupported: "Your browser does not support the HTML5 video tag.",
      closePanel: "Close sign-in panel",
      heroTitleManshu: "Manshu, where good stories get seen",
      heroTitleManjing: "Manjing, where good stories get seen",
      heroPrompt:
        "Generate characters, storyboards, and video visuals in one click",
      slogan: "Let every good story be seen by the world",
      titles: {
        welcomeManshu: "Welcome to Manshu",
        welcomeManjing: "Welcome to Manjing",
        register: "Create Account",
        bindPhone: "Bind Phone Number",
        password: "Reset Password",
      },
      tabs: {
        account: "Password Login",
        phone: "Code Login",
      },
      accountTypes: {
        enterprise: "Enterprise",
        personal: "Individual",
      },
      placeholders: {
        username: "Enter username",
        password: "Enter password",
        phone: "Enter phone number",
        code: "Enter verification code",
        companyName: "Enter company name",
        invitation: "Enter invitation code (optional)",
        newPassword: "Enter new password",
        confirmPassword: "Enter new password again",
      },
      actions: {
        login: "Sign In",
        register: "Sign Up",
        getCode: "Get Code",
        retryAfter: "Retry in {seconds}s",
        showPassword: "Show",
        hidePassword: "Hide",
        resetPassword: "Reset Password",
        bindAndLogin: "Bind and Sign In",
      },
      links: {
        forgotPassword: "Forgot password?",
        noAccount: "No account yet?",
        registerNow: "Sign up now",
        hasAccount: "Already have an account?",
        loginNow: "Sign in now",
        backToLogin: "Back to Sign In",
        backToLoginWithArrow: "← Back to Sign In",
      },
      agreement: {
        prefix: "By signing in, you agree to",
        terms: "User Agreement",
        and: "and",
        privacy: "Privacy Policy",
      },
      bindPhone: {
        tip: "Bind your phone number to keep your account secure",
        currentAccount: "Current account",
        desc: "This account has not bound a phone number. After binding, you can sign in quickly by phone and improve account security.",
        notice:
          "To keep your account secure, bind a phone number before using the platform.",
      },
      messages: {
        inviteLoadFailed: "Failed to load invitation code",
        enterPhoneFirst: "Enter your phone number first",
        enterPhone: "Enter your phone number",
        invalidPhone: "Enter a valid phone number",
        codeSent: "Verification code sent",
        enterCode: "Enter verification code",
        enterNewPassword: "Enter new password",
        enterConfirmPassword: "Enter the new password again",
        passwordMismatch: "The two new passwords do not match",
        passwordResetSuccess: "Password reset successfully",
        passwordResetFailed: "Failed to reset password",
        loginSuccess: "Signed in successfully",
        registerSuccess: "Registered successfully",
        invalidLoginState: "Invalid sign-in state. Please sign in again.",
        bindPhoneFailed: "Failed to bind phone number",
        enterInvitation: "Enter invitation code",
        enterCompanyName: "Enter company name",
        enterAccount: "Enter account",
        enterPassword: "Enter password",
      },
    },
    teamInvite: {
      close: "Close team invitation registration dialog",
      loading: "Loading invitation details...",
      retry: "Retry",
      loadFailed:
        "This invitation link is invalid or expired. Ask the inviter for a new one.",
      codeFailed: "Failed to send the verification code. Try again later.",
      registerFailed: "Registration failed. Try again later.",
      title: "Invitation link from {name}'s team",
      titleFallback: "Team invitation link",
      subtitle: "Complete registration to join the team with one click.",
      passwordMin: "Password must be at least 6 characters",
      agreementPrefix: "By registering, you agree to",
      details: {
        teamName: "Company",
        accountLevel: "Account Level",
        businessRole: "Business Role",
        parentAdmin: "Administrator",
      },
      fields: {
        phone: "Phone Number",
        code: "Verification Code",
        password: "Password",
        confirmPassword: "Confirm Password",
      },
    },
    partners: {
      title: "AIGC Creative Partners",
      desc: "Bringing together leading global AIGC creators, empowering creative expression with AI, and breaking the limits of traditional production.",
      featuredCreator: "Featured Creator",
      featuredWork: "Featured Work",
      featuredWorkAlt: "{name}'s featured work",
      creatorAria: "View {name}'s creator profile",
      items: {
        0: {
          name: "Xuanjiu",
          headline:
            "Top 10 Outstanding AIGC Figures of the 8th Chuxin Ranking (China Television Drama Production Industry Association, Capital Audio-Visual Industry Association)",
          bio: "Shortlisted for the IM Cross-Strait Youth Film Festival (Fujian Film Bureau, Fujian Radio and Television Bureau, Fujian Media Group)\n6th Malanshan Cup - Gold Award (hosted by Mango TV)\nParallel Worlds - Gold Award (hosted by Bilibili)\n3rd Shanghai International AIGC - Second Prize (hosted by Shanghai Jiading Nanxiang People's Government)\nWANMUSE+ Global Creation Contest Season 1 - Best Visual Award (hosted by Alibaba)",
        },
        1: {
          name: "Ning",
          headline:
            "Visual designer · AIGC creator\nAuthor of the Dayan Town series · Jimeng · Xiaoyunque · Kling Preferred Creator",
          bio: "· 2026 ADC 105th Annual Awards Bronze Winner\n· Li Bai in Anlu Poetry Short Video Contest, Video Category First Prize\n· Futang October 10 AIGC Global Short Video Contest First Prize\n· 2025 Team Championship Prompt Battle Gold Award\n· 2025 AI Vision · Imaging China Short Video Contest Silver Award\n· 2nd Global AI Visual Creativity VACAT Award, Image Category Excellence Award",
        },
        2: {
          name: "Xuankuan",
          headline:
            "AIGC advertising director · Story Relay MCN signed creator · AI + 3D printing farm partner",
          bio: "Signed artist for PixVerse Super Creator, Doubao, Jimeng Growth Program, Yuanji AI, and more\nMIIT AIGC professional certification · Former editor at Shanghai TV Documentary Channel · Shanghai Film AIGC lecturer\n\nPartner brands: IM Motors, Doubao, China Life, BYD, JD.com, Geely, Yanghe, Guotai, and more",
        },
        3: {
          name: "NONOPark",
          headline:
            "Visual director · AI video creation · NONO Park (Xuanzong Xuan Yige) · Deep collaboration with Yeahmobi · Title promos · E-commerce product videos",
          bio: "Hello, I am Xuanzong Xuan Yige, also known online as NONO Park. I am a visual director and content creator focused on AI video production. I work to combine frontier AI technology with visual art and explore new boundaries for video content production.\n\nIn AI visual creation, I have solid hands-on experience and commercial delivery capability. I previously entered the AI Boundless · Tech Empowerment AI Visual Creation competition hosted by Suzhou Science and Technology Park and was successfully shortlisted. That experience affirmed my AI creation ability and deepened my understanding of technology-enabled visual art.\n\nFor commercial collaboration, I currently maintain a deep partnership with Yeahmobi, a leading overseas marketing company. During the partnership, I am mainly responsible for AI short-video workflow testing, scaled production, and high-quality delivery. This has given me rich commercial project management experience and the ability to control each stage from early concept testing to final delivery.\n\nIn content creation, I specialize in AI-generated title promos and e-commerce product videos. By using AI video tools proficiently, I can greatly improve production efficiency while giving brands and products highly impactful, technology-driven visuals that support commercial conversion.\n\nI believe AI is the strongest brush for creators. I look forward to collaborating with more excellent teams and partners to explore the unlimited potential of AI video creation.",
        },
        4: {
          name: "Yida",
          headline:
            "Independent AIGC creator · MIIT-certified senior AIGC artist · Multi-platform super creator",
          bio: "Former head-office internal trainer at a state-owned bank\n\n· ISMA2025 Third Prize\n· Baidu AI Trend Season S1 Commercial Creativity Award\n· Awards from multiple domestic platform competitions",
        },
        5: {
          name: "MK",
          headline:
            "AIGC creator · Jimeng AI Growth Program · Douyin signed creator\nFlova.ai emerging creator",
          bio: "Deeply engaged in AIGC visual creation, skilled at using AI tools for visual expression and content production.\n\n· Haihean 1st AIGC Creative Challenge Blue Planet Innovation Award\n· 2024 World Artificial Intelligence Conference WAIC Wonderful World AI Painting Contest First Prize",
        },
        6: {
          name: "Xuan Sanshi",
          headline:
            "Leading figure in China's AIGC track · Hailuo AI / Paiwo AI super creator",
          bio: "With over 20 years of full-process film and television experience, Xuan Sanshi has served as technical director, executive director, chief director, and producer, leading more than 100 traditional film and television projects. As a signed writer on multiple platforms, they have published more than ten books totaling nearly ten million words, with works frequently topping rankings. They also hold AI music creation credentials and contributed to national 14th Five-Year Plan teaching materials. Awards span Beijing Film Academy, Greater Bay Area Film Festival, Silicon Valley AI Film Festival, NetEase, Tencent, and other major AIGC and film honors, showing strong technical, creative, and production capability.",
        },
        7: {
          name: "Xuanzong 2046",
          headline: "An ordinary editor",
          bio: "Awards: Excellence Award in the I AI Beijing event, with work featured by Beijing Daily. Excellence Award in the Wenzhou Xie Lingyun AI competition.",
        },
        8: {
          name: "Xuanzong Nigumao",
          headline:
            "Nigumao [high-quality creator across multiple platforms, Changzhi AI red-theme Gold Award, Lenghu sci-fi track Silver Award, Kling x Yuewen 10th Anniversary IP Light Contest Third Prize]",
          bio: "",
        },
        9: {
          name: "Xuanzong Baibaoge",
          headline:
            "Relaxed AI player · Top-three winner in multiple AIGC competitions",
          bio: "A relaxed AI player who has placed at least in the top three in competitions including the Taiwan Restoration 80th Anniversary and National Reunification AIGC Short Film Contest, Little Pony Crosses the River Cup AIGC Fable Contest, Langqiao AI Overseas Video Contest, Nanxi Creative Home AI Video Contest, and Yilu Tongxing China Charity Overseas AI Animation Short Film Contest. Smaller contests are not listed here.",
        },
        10: {
          name: "The Non-Myopic Cat",
          headline:
            "Public-account blogger with 10k+ followers · Hailuo AI super creator · Flova.ai super creator · Vidu artist · Kling quality creator",
          bio: "Best work in the dimensional track of the Weibo AI Video Creation Contest; Bronze Award in Baidu's Spring Festival Creation Contest; work screened as an Outstanding AI Film Work at the first Redstar Program Conference.",
        },
        11: {
          name: "Xuanzong Wangji",
          headline:
            "Kling outstanding creator; Peace Elite signed AIGC creator; Jianying AI Creative Video Phase 1 quality creator",
          bio: "",
        },
        12: {
          name: "Xuanzong Mitu",
          headline:
            "Media creator in the AIGC era · Technology as the brush · Creativity as the core",
          bio: "Explores new boundaries of video expression across government, media, and commercial tracks. Collaborations include Zhejiang Provincial Health Commission, Zhejiang Provincial Commission for Discipline Inspection, Henan Radio and Television, Hangzhou Daily, China Telecom, Geely Automobile, and dozens of other government and enterprise clients, creating multiple innovative AI video works. Works have won awards in AI video competitions hosted by Yili, Qipai, Hangzhou Daily, and other platforms.",
        },
        13: {
          name: "Liang Mila",
          headline:
            "AIGC director / digital artist / screenwriter / psychological counselor",
          bio: "Story Relay MCN signed creator | Super creator on LibTV, Manshu, and other platforms\n\nEntered the AIGC creation field in 2022, with works spanning narrative shorts, sci-fi visual effects, fantasy children's adventures, healing music videos, TVC ads, cultural-tourism promos, short dramas, IP fan works, and short-drama visual development.\n\nOne of the early AIGC explorers in 2022, with honors including First Prize in the Chongqing Spring Festival Gala AIGC Creative Contest, Excellence Award in Ride the Wind 2026 AIMV, Second Prize in the Kling AI Creative MV Challenge, Bronze Award in the VGS World AI Video Contest, and dozens of other awards or shortlistings. Film and TV IP fan works have received comments and interactions from related original creators.",
        },
        14: {
          name: "Xuanzong Cuiyixia",
          headline:
            "Chief AI designer of a large natural gas group | MIIT AI visual designer (senior)",
          bio: "Currently chief AI designer of a large natural gas group and MIIT AI visual designer (senior). AI video enthusiast.",
        },
        15: {
          name: "Xuanzong Shuizhixin",
          headline: "Founder of Xinshui Studio · AI and film-content innovator",
          bio: "Xinshui Studio (Shuizhixin) is a professional creative brand focused on AIGC visual storytelling. Founder Zhu Qin combines a strong science and engineering background from USTC with complete film-production experience, previous roles at Mango TV and Fortune Global 500 companies, and full-chain new-media entrepreneurship experience, bridging technology and content creation.\n\nThe brand focuses on AI short dramas, brand AI ads, AI films, and cultural-tourism promos, serving state-owned enterprises, healthcare clients, international brands, and more. It has produced multiple hit works with over 100 million views across the internet and formed a mature, repeatable AI content industrialization system.\n\nThe team has won the Malanshan AI Competition Gold Award, Global Digital Economy Innovation Competition Rising Star Award, and other honors. It integrates Eastern ink aesthetics with frontier AI technology to empower image creation intelligently and keep exploring new forms of digital visual expression.",
        },
      },
    },
    product: {
      heroImageAlt: "AIGC comic drama creation management platform",
      floatTags: {
        data: "Global content big data",
        insight: "Hit insight model",
        script: "AI script creation",
        asset: "Asset director model",
        video: "One-click video system",
      },
      eyebrow: "Products",
      titlePrefix: "AIGC Comic Drama Creation ",
      titleAccent: "Management Platform",
      subtitle:
        "From script to finished video, an end-to-end AI comic drama creation and management platform",
      cta: "Try Now",
      ctaHint: "Sign up to try core features for free",
      capabilities: "Six Core Capabilities",
      detail: "View Details",
      services: {
        data: {
          title: "AI Hit Comic Short Drama Big Data Platform",
          tagline: "A 4.2-trillion market. Move first.",
          desc: "A global multimodal data engine with hit prediction and trend detection, helping creators lock onto the next breakout title from topic selection to production planning.",
        },
        production: {
          title: "AI Film, Animation, and Comic Production Platform",
          tagline: "From script to finished video, delivered fast",
          desc: "AI parses scripts into storyboards, reuses character assets across an entire series, and covers text-to-image, image-to-video, storyboard slicing, and composite preview workflows.",
        },
        copyright: {
          title: "One-Stop Copyright Registration Platform",
          tagline: "AI work ownership, confirmed fast",
          desc: "Solves copyright registration for AI scripts so each work can be protected as it is generated, building long-term value for large IP libraries.",
        },
        filing: {
          title: "One-Click Comic/Short Drama Filing Platform",
          tagline: "Scarce licenses, exclusive channels, compliant delivery",
          desc: "Connects online audiovisual licenses and short-drama filing capabilities so creators can complete compliance filing in one click and focus on creation.",
        },
        distribution: {
          title: "Global Multi-Platform Distribution",
          tagline: "Distribute to 100+ countries in one click",
          desc: "AI translation, dubbing, and overseas platform coverage take content from local to global with one production and one distribution flow.",
        },
        commercialization: {
          title: "IP Derivative Commercialization",
          tagline: "Turn content into assets and unlock long-tail value",
          desc: "Character IP incubation, virtual avatar licensing, consumer product development, and global copyright trading create a closed loop for content monetization.",
        },
      },
    },
    about: {
      title: "About Us",
      vision: "Vision: Let every good story be seen by the world.",
      mission:
        "Mission: Reshape content industrialization with AI and empower creators worldwide.",
      contact: "Contact Us",
      customerServiceWechat: "Customer Service WeChat",
      customerServiceWechatQr: "Customer Service WeChat QR code",
      follow: "Follow Us",
      socialQrAlt: "{name} QR code",
      companyAddress:
        "Hefei Manyu Dianfeng Technology Co., Ltd. | Address: 2/F, Block B, Yunzhang Building, No. 535 Wangjiang West Road, Hefei, Anhui",
      social: {
        douyin: "Douyin",
        videoAccount: "WeChat Channels",
        wechatOfficial: "WeChat Official Account",
        xiaohongshu: "Xiaohongshu",
        weibo: "Weibo",
        bilibili: "Bilibili",
      },
      paragraphs: [
        "Manshu is built by Hefei Manyu Dianfeng Technology Co., Ltd. to create a global industrialized ecosystem for AI film, television, and animation, and to build a leading fast-fashion engine for AI content. The company was co-founded by top Chinese film and advertising marketing big-data experts, first-class animation production teams, and experienced capital operation teams.",
        "The Manshu ecosystem covers global content big-data SaaS, an AI creative brain center, comic short drama production management SaaS, hit-content prediction models, hit-content factories, comic short drama commercialization, industry-education integration, content copyright registration, broadcast and filing management based on online audiovisual licenses, and global content distribution.",
        "Manshu focuses on solving the full-chain challenge of helping creators work faster, create better, sell successfully, and earn more.",
        "The company's eight business matrices are connected and mutually reinforcing. Three SaaS editions cover creators and potential creators across the industry, with big-data-based film and animation production tools at the core, extending into film, animation, advertising marketing, and education.",
        "The core team participates in the compilation of 12 national AIGC textbooks for the 15th Five-Year Plan and is the only enterprise participant in that work. With its original ZARA content factory model, Manshu creates large-scale original IP and global distribution, while copyright registration qualifications and online audiovisual licenses form durable competitive barriers.",
        "Manshu has developed three film and television marketing models: Manyutop-Mind for text, Manyutop-Vision for images, and Manyutop-Motion for video. Through fine-tuning, domain knowledge bases, and industry skills on top of general models, it builds stronger domain capabilities for future market growth.",
        "Manshu serves comic short drama companies, film and television companies, creators, MCNs, advertising agencies, media organizations, brands, and more.",
      ],
    },
  },
  auth: {
    loginExpired: "Your session has expired. Please sign in again.",
    otherDeviceLogin:
      "This account is signed in on another device. Please check your account security.",
    accountFrozen:
      "This account has been frozen. Please contact your administrator.",
  },
  route: {
    home: "Home",
    scriptPage: "Script",
    storyboard: "Storyboard Editor",
    scriptPreview: "Storyboard Preview",
    copywritingCreation: "Script Creation",
    detectionResult: "Detection Results",
    myProject: "My Projects",
    projectManagement: "Clip Management",
    hotRanking: "Trending Rankings",
    statisticalDimension: "Analytics",
    visionCanvas: "Canvas",
    visionCanvasEditor: "Canvas Editor",
    projectGroupWorkbench: "Project Group Workbench",
    roleManagement: "Character Management",
    sceneManagement: "Scene Management",
    propManagement: "Prop Management",
    login: "Sign In",
    projectGroupInvite: "Project Group Invite",
    userManagement: "User Management",
    teamManagement: "Team Management",
    invoice: "Order Management",
  },
  invoiceCenter: {
    view: "View",
    reissue: "Reissue",
    resend: "Resend to Email",
    resendSuccess: "Invoice email resent",
    resendFailed: "Failed to resend the invoice email. Please try again later.",
    resendConfirm: {
      title: "Resend to Email",
      message: "Resend this invoice to the recipient's email address?",
    },
    reissueDialog: {
      title: "Reissue Invoice",
      submit: "Confirm Reissue",
      loadFailed: "Failed to load the original invoice. Please try again later.",
      submitSuccess: "Reissue request submitted",
      submitFailed: "Failed to submit the reissue request. Please try again later.",
    },
    detail: {
      title: "Invoice Details",
      invoiceInfo: "Invoice Information",
      orderInfo: "Order Information",
      purchaserType: "Purchaser Type",
      taxpayerNumber: "Taxpayer ID",
      invoiceCode: "Invoice Code",
      invoiceNumber: "Invoice Number",
      allElectronicInvoiceNumber: "Digital Invoice Number",
      failureReason: "Failure Reason",
      invoiceFile: "Invoice File",
      download: "Download Invoice",
      orderAmount: "Invoiced Amount",
      emptyOrders: "No linked orders",
      close: "Close",
      loadFailed: "Failed to load invoice details. Please try again later.",
    },
    title: "Order Management",
    subtitle: "View invoiceable orders and invoice history",
    tabs: {
      paidOrders: "Paid Orders",
      history: "Invoice History",
    },
    refresh: "Refresh",
    loading: "Loading",
    emptyOrders: "No paid orders",
    emptyHistory: "No invoice history",
    loadFailed: "Failed to load invoice data. Please try again later.",
    columns: {
      orderNo: "Order No.",
      payDescription: "Order Description",
      payTime: "Paid At",
      paidAmount: "Paid Amount",
      invoiceAmount: "Invoice Amount",
      orderStatus: "Invoice Status",
      applyNo: "Application No.",
      purchaserName: "Invoice Title",
      invoiceStatus: "Status",
      createTime: "Applied At",
      issueTime: "Issued At",
      actions: "Actions",
    },
    apply: "Invoice",
    unavailable: "This order is not invoiceable",
    submitSuccess: "Invoice application submitted",
    submitFailed: "Failed to submit the invoice application. Please try again later.",
    dialog: {
      title: "Request Invoice",
      orderNo: "Order No.",
      amount: "Invoice Amount",
      purchaserType: "Purchaser Type",
      personal: "Individual",
      enterprise: "Business",
      category: "Invoice Type",
      normalInvoice: "Digital General Invoice",
      specialInvoice: "Digital Special Invoice",
      purchaserName: "Invoice Title",
      purchaserNamePlaceholder: "Enter a personal or business name",
      taxpayerNumber: "Taxpayer ID (Required for Business)",
      taxpayerNumberPlaceholder: "Enter the business taxpayer ID",
      address: "Purchaser Address (Optional)",
      phone: "Purchaser Phone (Optional)",
      bank: "Bank Name (Optional)",
      bankAccount: "Bank Account (Optional)",
      mobile: "Mobile (Optional)",
      mobilePlaceholder: "For electronic invoice notifications",
      email: "Email (Optional)",
      emailPlaceholder: "For electronic invoice notifications",
      cancel: "Cancel",
      submit: "Submit",
    },
    validation: {
      purchaserType: "Select a purchaser type",
      category: "Select an invoice type",
      purchaserName: "Enter an invoice title",
      taxpayerNumber: "Enter the taxpayer ID",
      email: "Enter a valid email address",
    },
    status: {
      UNINVOICED: "Not Invoiced",
      SUBMITTING: "Pending Submission",
      ISSUING: "Issuing",
      ISSUED: "Issued",
      RED_PROCESSING: "Red Invoice Processing",
      ISSUE_FAILED: "Issue Failed",
      RED_REQUESTED: "Red Invoice Processing",
      RED_ISSUED: "Red Invoice Completed",
      RED_FAILED: "Red Invoice Failed",
      REISSUE_WAITING_RED: "Waiting for Original Invoice Reversal",
    },
  },
  workflow: {
    steps: {
      script: "Script",
      assets: "Assets",
      storyboard: "Storyboard Video",
      compose: "Compose Video",
    },
    actions: {
      next: "Next",
      parseScript: "Parse Script",
      autoStoryboard: "Auto Storyboard",
      preview: "Preview",
    },
    script: {
      projectName: "Project Name",
      episode: "Episode",
      aspectRatio: "Aspect Ratio",
      ratio: "Ratio",
      style: "Style",
      myScript: "My Script",
      placeholder: "Enter the script for this episode (optional)",
      defaultSettingsTip:
        "These settings will be used as defaults for later creation and can be changed. Enter a script to extract assets and storyboards automatically, or continue without one and create them manually.",
      assetNextTip: "Click Next to create storyboard images and videos.",
      assetAutoTip:
        "Click Auto Storyboard to generate storyboard images and videos from the script and assets.",
    },
    scriptPage: {
      previewImage: "Preview image",
      favorite: "Favorite",
      favoriteImage: "Favorite Image",
      selectImage: "Select Image",
      selectVideo: "Select Video",
      records: "History",
      materials: "Materials",
      favorited: "Favorites",
      selected: "Selected",
      uploadImage: "Upload Image",
      dragFile: "Drag a file here, or ",
      clickUpload: "click to upload",
      imageAlt: "Image {id}",
      videoAlt: "Video {id}",
      imageGenerationFailed: "Image generation failed",
      videoGenerationFailed: "Video generation failed",
      generating: "Generating...",
      imageRecordsHint:
        "Tip: Click a thumbnail to preview, the star to favorite, or the check mark to select.",
      videoRecordsHint:
        "Tip: Click a thumbnail to preview the video or the check mark to select it.",
      materialsHint:
        "Tip: Click a material to select it or Delete to remove it.",
      audioUnsupported: "Your browser does not support audio.",
      addImage: "Add Image",
      imageFormatHint: "PNG, JPG, JPEG, and BMP supported, up to 10 MB",
      outputImageCount: "Number of Images",
      outputVideoCount: "Number of Videos",
      generateImage: "Generate Image",
      generateVideo: "Generate Video",
      pointsCost: "Costs {count} points",
      materialImage: "Material image",
      audioMaterial: "Audio material",
      noPreview: "No preview available",
      draftVideo: "Draft video",
      imageSettings: "Image Generation Settings",
      videoSettings: "Video Generation Settings",
      imageCount: "Number of Images",
      videoCount: "Number of Videos",
      startGenerate: "Start Generating",
      noRecordsOrMaterials:
        "No history or materials yet. Generate or upload one.",
      batchGenerateMedia: "Batch Generate {media}",
      confirmBatchGenerate: "Batch generate {media}?",
      memberSettings: "Member Settings",
      username: "Username",
      usernamePlaceholder: "Enter a username",
      projectMembers: "Project members: {count}",
      assignImageAndVideo: "Assign both image and video storyboards",
      role: "Role",
      rejectData: "Reject Data",
      rejectReason: "Reason",
      rejectReasonPlaceholder:
        "Enter a rejection reason; press Ctrl+V to paste an image",
      uploading: "Uploading...",
      custom: "Custom",
      dialogueTypes: {
        lipSync: "Lip Sync",
        narration: "Narration",
        innerMonologue: "Inner Monologue",
      },
      shotSizes: {
        0: "Extreme Wide Shot",
        1: "Wide Shot",
        2: "Full Shot",
        3: "Medium Shot",
        4: "Medium Close-up",
        5: "Close-up",
        6: "Extreme Close-up",
      },
      pointsFailed: "Failed to retrieve the points cost for {media}",
      imageMax5MB: "Images cannot exceed 5 MB",
      approveConfirm: "Approve the current data?",
      approveTitle: "Confirm Approval",
      selectAssignee: "Select a member to assign",
      assignmentSuccess: "Assigned successfully",
      selectStoryboardsToAssign: "Select storyboards to assign",
      imageRequestFailed: "Image generation request failed",
      requestFailed: "Request failed 😿",
      addLastFrame: "Add a last-frame image",
      operationFailed: "Operation failed",
      voiceMissingTitle: "Voice Not Set",
      voiceMissingMessage:
        "Notice: Some characters do not have a voice selected. Storyboard prompts will not include audio references for those characters, so you will need to add their voices manually. Continue splitting the storyboard?",
      continueStoryboardSplit: "Continue",
      taskTimeout:
        "Processing timed out. Refresh the page later to check progress.",
      storyboardGenerationFailed: "Storyboard generation failed",
      scriptParsingFailed: "Script parsing failed",
      supportedMaterialTypesOnly:
        "Only audio, video, or image materials are supported",
      uploadFailedWithReason: "Upload failed: {reason}",
      serverError: "Server error",
      imageUploadFailed: "Image upload failed",
      imageUploadingWait:
        "The image is uploading. Wait for it to finish before saving.",
      imageMustUploadBeforeSave:
        "The image must finish uploading before you can save.",
    },
    storyboard: {
      images: "Storyboard Images",
      videos: "Storyboard Videos",
      imageTab: "Image",
      videoTab: "Video",
      item: "Storyboard",
      itemName: "Storyboard {label}",
      list: "Storyboard List",
      add: "Add Storyboard",
      batchDownload: "Batch Download",
      composeTip:
        "Preview the storyboard video sequence, or automatically edit the existing storyboard videos into a complete video.",
    },
    storyboardList: {
      expandPrompt: "Expand",
      collapsePrompt: "Collapse",
      noPrompt: "No prompt",
      editStoryboard: "Edit Storyboard",
      splitStoryboard: "Split Storyboard",
      more: "More",
      waitingVideo: "Waiting for video generation",
      generatingProgress: "Generating {current}/{total}",
      labels: {
        model: "Model",
        shotSize: "Shot Size",
        dialogue: "Dialogue",
        characters: "Characters",
        ratio: "Aspect Ratio",
        voice: "Voice",
        scenes: "Scenes",
        props: "Props",
        operator: "Operator",
        reviewer: "Reviewer",
        cameraMovement: "Camera Movement",
        lipSync: "Lip Sync",
        videoMode: "Video Mode",
      },
      yes: "Yes",
      no: "No",
      videoModes: {
        prompt: "Omni Reference",
        firstFrame: "First Frame",
        firstLast: "First and Last Frames",
      },
      media: {
        image: "image",
        video: "video",
        audio: "audio",
      },
      submitReview: "Submit for Review",
      approve: "Approve",
      reject: "Reject",
      insertBefore: "Insert before this shot",
      insertAfter: "Insert after this shot",
      messages: {
        selectStoryboard: "Select at least one storyboard",
        selectStyle: "Select a style",
        batchConfirm:
          "Batch generation will use the selected mode and model for all items and overwrite<br>edited storyboard content. Continue?",
        batchStarted: "Batch generation started and is being processed...",
        batchRequestFailed: "Batch generation request failed",
        noPreviewVideos:
          "No storyboard videos are available for preview. Generate storyboard videos first.",
        previewFailed: "Failed to load preview",
        noComposeVideos:
          "No storyboard videos are available to compose. Generate storyboard videos first.",
        composeFailed: "Failed to compose video",
        reviewTitle: "Submit for review?",
        reviewConfirm:
          "After submission, this item cannot be edited until an administrator approves or rejects it.",
        downloadFailed: "Download failed",
        deleteConfirm: "Delete this storyboard?",
        deleteMediaConfirm: "Delete this {media}?",
        added: "Added successfully",
        deleted: "Deleted successfully",
      },
    },
    batchGenerate: {
      close: "Close",
      selectModel: "Select Model",
      selectVersion: "Select Version",
      resolution: "Resolution",
      style: "Style",
      ratio: "Aspect Ratio",
      background: "Background",
      backgroundAuto: "Auto",
      backgroundOpaque: "Keep background",
      backgroundTransparent: "Transparent",
      duration: "Duration",
      noModels: "No models available",
      noVersions: "No versions available",
      noResolutions: "No resolutions available",
      noStyles: "No styles available",
      noRatios: "No aspect ratios available",
      generate: "Batch Generate",
      calculating: "Calculating",
      imageTitle: "Batch Generate Storyboard Images",
      videoTitle: "Batch Generate Storyboard Videos",
      firstFrame: "First Frame",
      referenceVideo: "Reference-to-Video",
      all: "All",
      pointsFailed: "Failed to calculate batch generation points",
      validation: {
        model: "Select a model",
        version: "Select a version",
        resolution: "Select a resolution",
        style: "Select a style",
        ratio: "Select an aspect ratio",
        duration: "Select a duration",
      },
    },
    previewPanel: {
      loading: "Loading video",
      empty: "No storyboard videos available for preview",
      model: "Model",
      ratio: "Aspect Ratio",
      quality: "Quality",
      style: "Style",
      noStyleLimit: "No style limit",
      compose: "Compose Video",
      missingScriptParams: "Missing script parameters",
      noVideo: "No video",
      storyboard: "Storyboard {id}",
      download: "Download composed video",
      composing: "Composing video {progress}%",
      loadingComposer: "Loading video composer",
      filename: "video-{id}.mp4",
      downloadStarted: "Video download started",
      downloadFailed: "Download failed",
      errors: {
        network: "Network error. Unable to connect to the preview service.",
        resourceLoad:
          "Failed to load the video. The link may be invalid or blocked by cross-origin restrictions.",
        composerNetwork:
          "Failed to load the video composer. Check your network and try again.",
        composerRefresh:
          "Failed to load the video composer. Refresh the page and try again.",
        apiNoResponse:
          "The preview service did not respond. Check your network or backend service.",
        fetchFailed: "Failed to load preview",
        playbackFailed: "Failed to play the composed video",
      },
    },
    storyboardWorkspace: {
      storyboard: "Storyboard",
      storyboardName: "Storyboard {label}",
      selectShot: "Select a storyboard to start editing",
      expand: "Expand",
      collapse: "Collapse",
      expandList: "Expand storyboard list",
      collapseList: "Collapse storyboard list",
      firstFrame: "First Frame",
      lastFrame: "Last Frame",
      reference: "Reference",
      expandAllReferences: "Expand all",
      collapseReferences: "Collapse",
      select: "Select",
      addToReferences: "Add to References",
      download: "Download",
      fullscreen: "Fullscreen",
      draft: "Draft",
      failed: "Failed",
      notGenerated: "Not Generated",
      history: "Generation History",
      noHistory: "No generation history",
      generate: "Generate",
      regenerate: "Regenerate",
      promptOptimize: "Optimize prompt",
      promptOptimizeResultTitle: "Optimized prompt",
      promptOptimizeFill: "Fill into input",
      promptOptimizeCopy: "Copy",
      promptOptimizeCopied: "Copied",
      promptOptimizeCopyFailed: "Copy failed",
      promptOptimizeFailed: "Prompt optimization failed",
      promptOptimizeEmpty: "No optimized prompt returned",
      promptOptimizeEmptyPrompt: "Please enter a prompt first",
      generateFailed: "Failed to generate {media}",
      paramsMissing: "Storyboard parameters are missing",
      unsavedLeaveTitle: "Unsaved storyboards",
      unsavedLeaveMessage:
        "These storyboards have unsaved changes: {shots}. Leaving will clear the local drafts. Continue?",
      confirmLeave: "Leave",
      continueEditing: "Keep Editing",
      roleAssetReviewTitle: "Character Image Review",
      roleAssetReviewMessage:
        "Some referenced characters have images that have not passed manual review. Continue generating?",
      continueGenerate: "Continue Generating",
      firstLastPromptReferenceInvalid:
        "Reference images mentioned in the prompt are ignored in first-and-last-frame mode. Continue generating?",
      roleAssetCheckFailed: "Failed to check character image review status",
      media: { image: "Image", video: "Video", audio: "Audio" },
      /** Single-file upload limits for storyboard references */
      fileValidate: {
        listSeparator: ", ",
        lessThan: "less than",
        noMoreThan: "no more than",
        kind: { image: "Image", video: "Video", audio: "Audio" },
        onlyMedia: "Only image, video, or audio is supported",
        unrecognizedFormat: "Unrecognized file format",
        imageFormatOnly:
          "Images only support jpeg, png, webp, bmp, tiff, gif, heic/heif",
        videoFormatOnly: "Videos only support mp4, mov",
        audioFormatOnly: "Audio only supports wav, mp3",
        sizeNeed:
          "{kind} size must be {comparator} {size} (actual: {actual})",
        cannotReadImageSize:
          "Unable to read image size. Please check if the file is corrupted",
        cannotDecodeImageFormat:
          "Unable to decode this {format} image. Please convert it to jpeg or png and try again",
        cannotReadVideoSize:
          "Unable to read video size. Please check if the file is corrupted",
        imageWidthTooSmall:
          "Image width is under {min}px. Please enlarge or choose another image",
        imageWidthTooLarge:
          "Image width exceeds {max}px. Please resize and try again",
        imageHeightTooSmall:
          "Image height is under {min}px. Please enlarge or choose another image",
        imageHeightTooLarge:
          "Image height exceeds {max}px. Please resize and try again",
        videoEdgeRange: "Video width/height must be between {min}–{max}px",
        imageAspectRange: "Image aspect ratio must be between {min}–{max}",
        videoAspectRange: "Video aspect ratio must be between {min}–{max}",
        videoPixelsRange: "Video total pixels must be between {min}–{max}",
        videoPixelsTooLarge:
          "currently about {actualLabel} ({width}×{height}), which exceeds the allowed range (about {range})",
        videoPixelsTooSmall:
          "currently about {actualLabel} ({width}×{height}), which is below the allowed range (about {range})",
        videoResolutionOnly:
          "Video resolution must be between {resolutions} (current: {actual})",
        cannotReadVideoDuration:
          "Unable to read video duration. Please check if the file is corrupted",
        cannotReadAudioDuration:
          "Unable to read audio duration. Please check if the file is corrupted",
        videoDurationRange:
          "Video duration must be between {min}–{max}s (actual: {actual}s)",
        audioDurationRange:
          "Audio duration must be between {min}–{max}s (actual: {actual}s)",
        cannotReadVideoFps:
          "Unable to read video frame rate. Please check if the file is corrupted",
        videoFpsRange:
          "Video frame rate must be between {min}–{max} FPS (actual: {actual})",
        untitledFile: "Untitled file",
        fileFailedDetail: '"{fileName}" {message}',
      },
      messages: {
        noOperableRecord: "No generation record is available for this action",
        selectSuccess: "Selected successfully",
        selectCancelled: "Selection cancelled",
        operationFailed: "Operation failed",
        noDeletableRecord: "No generation record is available to delete",
        deleteSuccess: "Deleted successfully",
        deleteFailed: "Failed to delete",
        deleteDraftFailed: "Failed to delete draft",
        noSavableImage: "No generated image is available to save",
        referenceLimitReached:
          "The reference limit of {count} has been reached. Remove a reference before adding another.",
        referenceImageModeLimitReached:
          "The reference limit of {count} images has been reached. Remove a reference before adding another.",
        referenceImageLimitReached:
          "The image reference limit of {count} has been reached. Remove a reference before adding another.",
        referenceVideoLimitReached:
          "The video reference limit of {count} has been reached. Remove a reference before adding another.",
        referenceAudioLimitReached:
          "The audio reference limit of {count} has been reached. Remove a reference before adding another.",
        unsupportedMedia: "Only images, videos, or audio files are supported",
        addedToReferences: "Added to reference materials",
        addFailed: "Failed to add",
        noDownloadable: "No {media} is available to download",
        promptPlaceholder: "Enter a prompt",
        referenceMaterial: "Reference material",
        uploadFailed: "Upload failed",
        paramsMissing: "Storyboard parameters are missing",
        imageOnlyMode: "Only images can be uploaded in the current mode",
        imageModeImagesOnly: "Only images can be uploaded in image mode",
        referenceBatchMax10: "You can upload up to {count} files at a time",
        getModelsFailed: "Failed to load the model list",
        getModelParamsFailed: "Failed to load model parameters",
        getPointsFailed: "Failed to retrieve the points cost",
        selectStyle: "Select a style",
        addFirstLastFrames: "Add the first- and last-frame images",
        addFirstFrame: "Add a first-frame image",
        saveFailed: "Failed to save",
        saveSuccess: "Saved successfully",
        generatingInProgress:
          "This storyboard is currently being generated. Try again after generation is complete.",
        generateStarted: "Generation started",
        generateFailed: "Generation failed",
        /** Save/generate cancelled because the storyboard, tab or history record changed during pre-submit checks */
        generateCancelledOnSwitch:
          "You switched storyboards or history records while submitting, so generation for Storyboard {label} was cancelled. Go back to it and generate again.",
        saveCancelledOnSwitch:
          "You switched storyboards or history records while submitting, so saving Storyboard {label} was cancelled. Go back to it and save again.",
      },
      modes: {
        reference: "Omni Reference",
        firstLast: "First & Last Frames",
        first: "First Frame",
      },
      promptHint: {
        reference:
          "Upload references. Prompt: type “@” to mention characters, scenes, props, or materials.",
        firstFrame:
          "Prompt: Upload an image as the first frame. Type “@” to mention characters, scenes, props, or materials.",
        firstLast:
          "Upload images as the first and last frames. Prompt: type “@” to mention characters, scenes, props, or materials.",
      },
      reviewStatus: {
        legend:
          "Uploaded references are automatically reviewed for human likeness. Green means approved, red means review failed, and yellow means under review",
      },
      config: {
        noModels: "No models available",
        noStyles: "No styles available",
        resolution: "Resolution",
        ratio: "Ratio",
        background: "Background",
        backgroundAuto: "Auto",
        backgroundOpaque: "Keep background",
        backgroundTransparent: "Transparent",
        quality: "Quality",
        duration: "Video Duration",
        quantityValue: "{count}",
        quantityValueVideo: "{count}",
        points: "Points",
        selectModel: "Select Model",
        selectStyle: "Select Style",
        allStyles: "All Styles",
        parameters: "Parameters",
      },
      audioPreview: "Audio Preview",
      videoModelLimits: {
        maxImages: "This model cannot reference more than {count} images",
        maxVideos: "This model cannot reference more than {count} videos",
        maxAudios: "This model cannot reference more than {count} audio files",
        maxTotal:
          "This model cannot reference more than {count} images, videos, and audio files in total",
        maxVideoDuration:
          "Total referenced video duration for this model cannot exceed {seconds}s",
        maxAudioDuration:
          "Total referenced audio duration for this model cannot exceed {seconds}s",
        audioNotSupported: "This model does not support audio references",
        maxImageAndVideo:
          "This model cannot reference more than {count} images and videos in total",
        audioVideoNotSupportedAsReference:
          "This model does not support audio or video as references",
        untitledFile: "Untitled file",
        fileFailedDetail: 'Current model: "{fileName}" {message}',
        animatedNotSupported:
          "This model does not support animated images as references",
        audioOnlyNotSupported:
          "This model's referenced materials cannot be audio only",
        klingPromptTooLong:
          "The current model prompt cannot exceed {max} characters",
      },
      imageModelLimits: {
        maxImages: "This model cannot reference more than {count} images",
        animatedNotSupported:
          "This model does not support animated images as references",
      },
      mention: {
        search: "Search by keyword",
        empty: "No data",
        tabs: {
          reference: "References",
          asset: "Assets",
          material: "Materials",
        },
        assetFilters: { role: "Characters", scene: "Scenes", prop: "Props" },
        assetSource: { script: "Clip", project: "Project" },
        materialFilters: {
          all: "All",
          other: "Other",
          character: "Characters",
          scene: "Scenes",
          item: "Items",
          style: "Styles",
          sound: "Audio",
          project: "Project Space",
        },
        // 同一提示词里素材与其他 @ 重名时的自动改名；用全角括号、不带空格，避免影响 #名字# 解析
        sameNameAlias: {
          image: "{name}（Image）",
          video: "{name}（Video）",
          audio: "{name}（Audio）",
        },
        sameNameAliasIndexed: {
          image: "{name}（Image{index}）",
          video: "{name}（Video{index}）",
          audio: "{name}（Audio{index}）",
        },
      },
    },
    compose: {
      generating: "Generating video",
      videoUnsupported: "Your browser does not support HTML5 video.",
    },
    assets: {
      selectAssignData: "Select data to assign",
      batchStarted: "Batch generation started",
      batchFailed: "Batch generation failed",
      generatingCannotDelete:
        "Asset is generating. Please wait until generation finishes before deleting.",
      noModels: "No models available",
      roles: {
        list: "Character List",
        more: "Add Characters",
        batchTitle: "Batch Generate Characters",
        searchPlaceholder: "Search character names",
        empty: "No characters yet",
        emptyTip: "Click the button below to create your first character",
        emptySearch: "No matching characters found",
        emptySearchTip: "Try searching with different keywords",
        create: "Create Character",
        deleteConfirm: "Delete this character?",
        selectBatchWarning: "Select the characters to generate in batch first",
        gender: { unlimited: "Any", male: "Male", female: "Female" },
        age: {
          unknown: "Unknown",
          childhood: "Child",
          child: "Child",
          teen: "Teen",
          young: "Young Adult",
          middle: "Middle-aged",
          senior: "Senior",
        },
        dialog: {
          createTitle: "Create Character Image",
          editTitle: "Edit Character Image",
          closeTip: "Closing this window will not interrupt ongoing tasks",
          close: "Close",
          name: "Character Name",
          gender: "Gender",
          age: "Age Group",
          enter: "Enter a value",
          select: "Select an option",
          generationMode: "Generation Method",
          generateByModel: "Generate with AI",
          uploadImage: "Upload an Image",
          addImage: "Add Image",
          formatHint: "PNG, JPG, JPEG, and BMP supported, up to 10 MB",
          model: "Model",
          ratio: "Aspect Ratio",
          resolution: "Resolution",
          style: "Style",
          prompt: "Prompt",
          promptPlaceholder:
            "Describe the character's appearance, including gender, age, clothing, demeanor, and other details.",
          uploadReference: "Upload Reference",
          generateImage: "Generate Image",
          generatingWarning:
            "Do not close this window while images are being generated, as this may interrupt generation.",
          saveChanges: "Save Changes",
          voice: "Voice",
          preview: "Preview",
          pausePreview: "Pause",
          imageCount: "Number of Images",
          imageCountValue: "{count} image(s)",
          startGenerate: "Start Generating",
          calculating: "Calculating",
          records: "Generation History",
          recordsHint:
            "Select an image from the history, then save to apply it.",
          generationFailed: "Image generation failed",
          failed: "Failed",
          noRecords: "No history yet",
          saveSettings: "Save Settings",
          validation: {
            name: "Enter a character name",
            gender: "Select a gender",
            age: "Select an age group",
            prompt: "Enter a prompt",
            model: "Select a model",
            ratio: "Select an aspect ratio",
            resolution: "Select a resolution",
            style: "Select a style",
          },
          messages: {
            singleFileOnly:
              "Only one file can be uploaded. Delete the current file before uploading another.",
            loginExpired: "Your session has expired. Please sign in again.",
            uploadFailed: "Upload failed",
            imageUploadFailed: "Image upload failed",
            invalidFormat: "Unsupported file format",
            fileTooLarge: "The image must be 10 MB or smaller",
            missingImageId: "Image record ID is missing",
            deleteConfirm: "Delete this image?",
            deleteSuccess: "Deleted successfully",
            pointsFailed: "Failed to calculate points cost",
            operationFailed: "Operation failed",
            saveFailed: "Save failed",
            selectCompletedBeforeSave:
              "Select a completed image before saving",
            previewUnavailable: "No preview audio is available for this voice",
            previewFailed: "Failed to preview voice",
          },
        },
      },
      scenes: {
        list: "Scene List",
        more: "Add Scenes",
        batchTitle: "Batch Generate Scenes",
        searchPlaceholder: "Search scene names",
        empty: "No scenes yet",
        emptyTip: "Click the button below to create your first scene",
        emptySearch: "No matching scenes found",
        emptySearchTip: "Try searching with different keywords",
        create: "Create Scene",
        deleteConfirm: "Delete this scene?",
        selectBatchWarning: "Select the scenes to generate in batch first",
        dialog: {
          createTitle: "Create Scene",
          editTitle: "Edit Scene",
          closeTip: "Closing this window will not interrupt ongoing tasks",
          close: "Close",
          name: "Scene Name",
          namePlaceholder: "Enter a scene name",
          select: "Select an option",
          generationMode: "Generation Method",
          generateByModel: "Generate with AI",
          uploadImage: "Upload an Image",
          addImage: "Add Image",
          formatHint: "PNG, JPG, JPEG, and BMP supported, up to 10 MB",
          model: "Model",
          ratio: "Aspect Ratio",
          resolution: "Resolution",
          style: "Style",
          prompt: "Prompt",
          promptPlaceholder:
            "Describe the scene in detail, including space, time, weather, lighting, landmarks, background elements, environment, era, genre, art style, and camera angle.",
          uploadReference: "Upload Reference",
          generateImage: "Generate Image",
          generatingWarning:
            "Do not close this window while images are being generated, as this may interrupt generation.",
          saveChanges: "Save Changes",
          time: "Time",
          timePlaceholder: "e.g. dawn, noon, dusk, night...",
          weather: "Weather",
          weatherPlaceholder:
            "e.g. sunny, cloudy, light rain, heavy rain, mist...",
          light: "Lighting",
          lightPlaceholder:
            "e.g. daylight, warm light, moonlight, candlelight, backlight...",
          imageCount: "Number of Images",
          imageCountValue: "{count} image(s)",
          startGenerate: "Start Generating",
          calculating: "Calculating",
          records: "Generation History",
          recordsHint:
            "Select an image from the history, then save to apply it.",
          generationFailed: "Image generation failed",
          failed: "Failed",
          noRecords: "No history yet",
          saveSettings: "Save Settings",
          validation: {
            name: "Enter a scene name",
            prompt: "Enter a prompt",
            model: "Select a model",
            ratio: "Select an aspect ratio",
            resolution: "Select a resolution",
            style: "Select a style",
          },
          messages: {
            singleFileOnly:
              "Only one file can be uploaded. Delete the current file before uploading another.",
            loginExpired: "Your session has expired. Please sign in again.",
            uploadFailed: "Upload failed",
            invalidFormat: "Unsupported file format",
            fileTooLarge: "The image must be 10 MB or smaller",
            missingImageId: "Image record ID is missing",
            deleteConfirm: "Delete this image?",
            deleteSuccess: "Deleted successfully",
            pointsFailed: "Failed to calculate points cost",
            operationFailed: "Operation failed",
            saveFailed: "Save failed",
            selectCompletedBeforeSave:
              "Select a completed image before saving",
            uploadReference: "Upload a reference image",
          },
        },
      },
      props: {
        list: "Prop List",
        more: "Add Props",
        batchTitle: "Batch Generate Props",
        searchPlaceholder: "Search prop names",
        empty: "No props yet",
        emptyTip: "Click the button below to create your first prop",
        emptySearch: "No matching props found",
        emptySearchTip: "Try searching with different keywords",
        create: "Create Prop",
        deleteConfirm: "Delete this prop?",
        selectBatchWarning: "Select the props to generate in batch first",
        dialog: {
          createTitle: "Create Prop",
          editTitle: "Edit Prop",
          closeTip: "Closing this window will not interrupt ongoing tasks",
          close: "Close",
          name: "Prop Name",
          namePlaceholder: "Enter a prop name",
          select: "Select an option",
          generationMode: "Generation Method",
          generateByModel: "Generate with AI",
          uploadImage: "Upload an Image",
          addImage: "Add Image",
          formatHint: "PNG, JPG, JPEG, and BMP supported, up to 10 MB",
          model: "Model",
          ratio: "Aspect Ratio",
          resolution: "Resolution",
          style: "Style",
          prompt: "Prompt",
          promptPlaceholder: "Describe the prop you want to generate",
          uploadReference: "Upload Reference",
          generateImage: "Generate Image",
          generatingWarning:
            "Do not close this window while images are being generated, as this may interrupt generation.",
          saveChanges: "Save Changes",
          imageCount: "Number of Images",
          imageCountValue: "{count} image(s)",
          startGenerate: "Start Generating",
          calculating: "Calculating",
          records: "Generation History",
          recordsHint:
            "Select an image from the history, then save to apply it.",
          generationFailed: "Image generation failed",
          failed: "Failed",
          noRecords: "No history yet",
          saveSettings: "Save Settings",
          validation: {
            name: "Enter a prop name",
            prompt: "Enter a prompt",
            model: "Select a model",
            ratio: "Select an aspect ratio",
            resolution: "Select a resolution",
            style: "Select a style",
          },
          messages: {
            singleFileOnly:
              "Only one file can be uploaded. Delete the current file before uploading another.",
            loginExpired: "Your session has expired. Please sign in again.",
            uploadFailed: "Upload failed",
            invalidFormat: "Unsupported file format",
            fileTooLarge: "The image must be 10 MB or smaller",
            missingImageId: "Image record ID is missing",
            deleteConfirm: "Delete this image?",
            deleteSuccess: "Deleted successfully",
            pointsFailed: "Failed to calculate points cost",
            operationFailed: "Operation failed",
            saveFailed: "Save failed",
            selectCompletedBeforeSave:
              "Select a completed image before saving",
            uploadReference: "Upload a reference image",
          },
        },
      },
      review: {
        title: "Human Review",
        notSubmitted: "Not submitted for human review",
        reviewing: "Human review in progress",
        rejected: "Human review rejected",
        approved: "Human review approved",
        notReviewed: "Not reviewed",
        reviewingShort: "Under review",
        failed: "Review failed",
        approvedShort: "Approved",
        noError: "No error details available",
        confirmTitle: "Submit for review?",
        confirmMessage:
          "After submission, this item cannot be edited until an administrator approves or rejects it.",
        approveTitle: "Approve Item",
        approveMessage: "Approve this item?",
      },
      materials: {
        list: "Material List",
        upload: "Upload Material",
        categoriesLabel: "Material Categories",
        preview: "Preview material {name}",
        categories: {
          all: "All",
          character: "Characters",
          scene: "Scenes",
          object: "Props",
          audio: "Audio",
          other: "Other",
        },
        dialog: {
          name: "Material Name",
          namePlaceholder: "Enter a material name",
          category: "Material Category",
          categoryPlaceholder: "Select a material category",
          file: "Upload File",
          fileRequired: "Select a file to upload",
          dragHere: "Drag a file here, or ",
          clickSelect: "click to select",
          editTitle: "Edit Material",
          add: "Add Material",
        },
        validation: {
          fileInvalid: "The file does not meet the upload requirements",
          uploadFailedReselect: "Upload failed. Please select the file again.",
          waitForUpload:
            "Wait for the upload to finish before adding the material.",
          unsupportedType:
            "Only image, video, or audio materials are supported",
          imageFormat:
            "Supported image formats: jpeg, png, webp, bmp, tiff, gif, heic/heif",
          videoFormat: "Supported video formats: mp4, mov",
          audioFormat: "Supported audio formats: wav, mp3",
          formatUnsupported: "Unsupported file format",
          imageSize: "The image must be smaller than 30 MB",
          videoSize: "The video must not exceed 50 MB",
          audioSize: "The audio must not exceed 15 MB",
          sizeExceeded: "The file exceeds the size limit",
          videoDurationUnreadable: "Unable to read the video duration",
          audioDurationUnreadable: "Unable to read the audio duration",
          videoDurationRange:
            "Video duration must be between {min} and {max} seconds",
          audioDurationRange:
            "Audio duration must be between {min} and {max} seconds",
          videoSizeUnreadable: "Unable to read the video dimensions",
          imageSizeUnreadable: "Unable to read the image dimensions",
          videoSideRange:
            "Video width and height must be between {min} and {max} px",
          imageSideRange:
            "Image width and height must be greater than {min} px and less than {max} px",
          videoRatioRange: "Video aspect ratio must be between {min} and {max}",
          imageRatioRange:
            "Image aspect ratio must be greater than {min} and less than {max}",
          videoResolution: "Supported video resolutions: 480p, 720p, 1080p",
          videoPixels: "Total video pixels must be between {min} and {max}",
          videoFpsUnreadable: "Unable to read the video frame rate",
          videoFpsRange: "Video frame rate must be between {min} and {max} FPS",
          videoInfoUnreadable: "Unable to read the video information",
          audioInfoUnreadable: "Unable to read the audio information",
          credentialFailed: "Failed to get upload credentials",
          saveUploadInfoFailed: "Failed to save the uploaded file information",
          credentialIncomplete: "Required upload credential fields are missing",
          uploadFailed: "Upload failed",
        },
        deleteConfirm: "Delete this material?",
        messages: {
          listFailed: "Failed to load materials",
          scopeMissing: "Material ownership information is missing",
          urlMissing: "Material file URL is missing",
          idMissing: "Material ID is missing",
          saveFailed: "Failed to save material",
          added: "Added successfully",
          deleteFailed: "Delete failed",
        },
      },
    },
    units: {
      currency: "HKD",
      episode: "ep.",
      second: "sec",
    },
    projects: {
      title: "My Projects",
      searchPlaceholder: "Search project names",
      empty: "No projects yet",
      emptyTip: "Create your first AI comic drama project below",
      noMatch: "No matching projects",
      tryAnotherKeyword: "Try searching with another keyword",
      create: "Create Project",
      add: "Add Project",
      actions: {
        statistics: "Asset Statistics",
        details: "Project Details",
        edit: "Edit Project",
        delete: "Delete Project",
        addMember: "Add Members",
      },
      statistics: {
        title: "Asset Statistics",
        roles: "Character Images",
        scenes: "Scene Images",
        props: "Prop Images",
      },
      details: {
        title: "Project Details",
        creator: "Creator Account",
      },
      form: {
        name: "Project Name",
        namePlaceholder: "Enter a project name",
        budget: "Project Budget",
        budgetPlaceholder: "Enter the project budget",
        episodes: "Episodes",
        episodesPlaceholder: "Enter the number of episodes",
        episodeDuration: "Duration per Episode",
        durationPlaceholder: "Enter the duration per episode",
        description: "Project Description",
        descriptionPlaceholder: "Enter a project description",
      },
      deleteConfirm: {
        message: "Delete this project? This action cannot be undone.",
        confirm: "Delete",
      },
    },
    management: {
      nav: {
        clips: "Episode Management",
        roles: "Character Management",
        scenes: "Scene Management",
        props: "Prop Management",
        materials: "Material Management",
      },
      clips: {
        searchPlaceholder: "Search episode names",
        empty: "No episodes yet",
        emptyTip: "Create your first episode below",
        noMatch: "No matching episodes",
        create: "New Episode",
        createDialog: "Create Episode",
        edit: "Edit Episode",
        delete: "Delete Episode",
        videoStatistics: "Video Statistics",
        storyboardVideos: "Storyboard Videos",
        episodeTitle: "Episode {name}",
        name: "Episode Name",
        namePlaceholder: "Enter an episode name",
        description: "Episode Description",
        descriptionPlaceholder: "Enter an episode description",
        deleteConfirm: {
          message: "Delete this episode? This action cannot be undone.",
          confirm: "Delete",
        },
      },
    },
    members: {
      settings: "Member Settings",
      usernamePlaceholder: "Enter a username",
      count: "Project members: {count}",
      user: "User",
      username: "Username",
      role: "Role",
      selectMember: "Select a member to assign",
    },
    returnData: {
      title: "Return Data",
      reason: "Return Reason",
      placeholder: "Enter a reason; press Ctrl + V to paste an image",
      imageTooLarge: "Images cannot exceed 5 MB",
    },
    review: {
      submit: "Submit for Human Review",
      refresh: "Refresh Review Status",
      hide: "Hide Review Status",
      show: "Show Review Status",
      submitSuccess: "Submitted for human review",
    },
    validation: {
      projectStatus: "Select a project status",
      number: "Enter a number",
      maxDigits: "Enter no more than 10 digits",
      positiveInteger: "Enter a positive integer greater than 0",
      projectNameRequired: "Project name is required",
      length2To20: "Must be 2-20 characters",
    },
    permissions: {
      noCreate: "You do not have permission to create items",
      notOwner: "You are not responsible for this data",
      noOperation: "You do not have permission for this action",
    },
    messages: {
      created: "Created successfully",
      updated: "Updated successfully",
      deleted: "Deleted successfully",
      assigned: "Assigned successfully",
      operationSuccess: "Operation successful",
      operationFailed: "Operation failed",
      uploadFailed: "Upload failed",
    },
  },
  scriptCreation: {
    insufficientPoints: {
      coin: "P",
      title: "Insufficient points",
      description:
        "Generating a script requires points. Your current balance is insufficient.",
      required: "/ {value} required",
      deficit: "{value} points short",
      recharge: "Recharge",
      refreshTip: "Refresh the page after recharging",
    },
    common: {
      back: "Back",
      cancel: "Cancel",
      loading: "Loading",
      loadingDots: "Loading...",
      script: "Script",
      outline: "Outline",
      scriptOutline: "Script Outline",
      notSelected: "Not selected",
      notFilled: "Not filled",
      custom: "Custom",
      unknown: "Unknown",
      listSeparator: ", ",
      episodeUnit: "episodes",
      minuteUnit: "min",
      episodeCount: "{count} episodes",
      durationMinutes: "{count} min",
    },
    workbench: {
      title: "Workbench",
      subtitle: "Welcome back. Start your creation journey.",
      startTitle: "Start Creating",
      startDesc: "Create a new script project",
      scriptsTitle: "My Scripts",
      projectCount: "{count} projects",
    },
    list: {
      title: "My Scripts",
      searchPlaceholder: "Search script name...",
      newScript: "New Script",
      loading: "Loading script list...",
      empty: 'No scripts yet. Click "New Script" to start creating.',
      view: "View",
      download: "Download",
      delete: "Delete",
      deleting: "Deleting",
    },
    saveSuccess: {
      saved: "Saved",
      title: "Saved successfully",
      desc: "Your script has been saved to the script library.",
      backWorkbench: "Back to Workbench",
      createComic: "Create Comic Drama",
      viewScripts: "View My Scripts",
    },
    steps: {
      story: { name: "One-line Story", desc: "Story seed" },
      genre: { name: "Genre Setup", desc: "Story direction" },
      params: { name: "Parameters", desc: "Visual settings" },
      outline: { name: "Script Outline", desc: "Preview and confirm" },
    },
    summary: {
      title: "Creation Settings",
      note: "AI will use these settings to tune script pacing, character relationships, and visual descriptions.",
      story: "One-line Story",
      genre: "Genre",
      subGenre: "Subgenre",
      creationStyle: "Writing Style",
      keywords: "Keywords",
      episodeCount: "Episodes",
      duration: "Episode Duration",
      ratio: "Aspect Ratio",
      resolution: "Resolution",
      videoStyle: "Video Style",
    },
    story: {
      label:
        "One-line story (AI can recommend genres and styles from it; you can continue without filling it in)",
      placeholder:
        "Describe the core setup, conflict, and hook. Example: A modern office worker accidentally travels to ancient times and falls in love with a cold prince.",
    },
    genre: {
      selectGenre: "Select Genre",
      customGenreLabel: "Custom genre (20 characters max)",
      customGenrePlaceholder: "Enter genre name...",
      selectSubGenre: "Select Subgenre",
      multiSelectHint: "Multiple selections allowed, separated by commas",
      loadingSubGenres: "Loading subgenres...",
      customSubGenrePlaceholder: "+ Custom subgenre",
      keywordTags: "Keyword Tags",
      loadingKeywords: "Loading keywords...",
      customKeywordPlaceholder: "+ Add custom keyword",
      creationStyle: "Writing Style",
    },
    params: {
      episodeTitle: "Episode Count",
      episodeDesc: "Set total episodes, up to {count} per run",
      durationTitle: "Episode Duration",
      durationDesc: "Video duration per episode",
      ratioTitle: "Aspect Ratio",
      resolutionTitle: "Resolution",
      videoStyleTitle: "Video Style",
      videoStyleHint: "Hover to preview",
      modelTitle: "Select Model",
    },
    outline: {
      summary: "Script Summary",
      worldview: "World Setting",
      theme: "Core Theme",
      summaryNote:
        "AI generated this outline from your one-line story. You can edit it directly.",
      editNameAria: "Edit script name",
    },
    detail: {
      backAria: "Back",
      model: "Model",
      parameters: "Parameters",
      messages: {
        noOutlineContent: "No outline content yet",
        episodeSaveFailed: "Failed to save episode",
        episodeDetailSaveFailed: "Failed to save episode details",
        characterSaveFailed: "Failed to save character",
        characterReferencedCannotEdit:
          "This character is referenced by an episode summary and cannot be edited",
        characterReferencedCannotDelete:
          "This character is referenced by an episode summary and cannot be deleted",
        characterCannotDelete: "This character cannot be deleted",
        characterReferencedCannotRename:
          "This character is referenced by an episode summary and cannot be renamed",
        characterNameExists: "Character name already exists",
        characterDeleteFailed: "Failed to delete character",
        newCharacterName: "New Character {n}",
        rolePending: "To be completed",
        characterDescPlaceholderDefault:
          "Please complete the character settings.",
        newAvatar: "N",
        personFallback: "P",
        goalLabel: "Goal: ",
        storyFunctionLabel: "Story function: ",
        roleTypePerson: "Character",
        protagonistTeam: "Protagonist Team",
        mainCharacter: "Protagonist",
        partner: "Partner",
        indoor: "Interior",
        infoHotspot: "Information Reveal",
        expectationHook: "Anticipation Hook",
        episodeListFailed: "Failed to load episode list",
        episodeDetailFailed: "Failed to load episode details",
        episodeDetailTimeout:
          "Episode detail generation timed out. Please refresh and check later.",
        episodeIdMissing:
          "Episode ID is missing. Cannot generate episode details.",
        selectTextModel: "Please select a text model first",
        mockEpisodePlot:
          "{description} This episode revolves around {people}, advancing character relationships through conflict and ending with a new mystery.",
        mockExplorerCommandBridge: "Explorer Command Bridge",
        mockMainConsoleArea: "Main Console",
        mockEpisodeScript:
          'Scene 1 · Interior · Explorer Command Bridge\nTime: Late night\n\n▽Warning lights flash slowly across the bulkhead, casting blue light over the characters’ faces.\n\n◆{protagonist}\n> [Cold] "All personnel, prepare to enter the unknown region. All departments, report status."\n\n▽Data streams cascade down the screen as new signal coordinates lock into place.\n\n◆{partner}\n> [Tense] "Similarity has reached 98.7%. I recommend dispatching an investigation team immediately."\n\n▽The protagonist falls silent, eyes fixed on the pulsing coordinates.\n\n◆{protagonist}\n> [Restrained] "Prepare the landing sequence. Assemble in the launch bay in ten minutes."\n\n▽The camera sweeps past the viewport as the outline of an unknown planet emerges from the darkness.\n\nEnd of episode.',
        mockHotspotDescription:
          "A highly similar signal is suddenly discovered during a routine investigation, emphasizing the mystery and intrigue of the unknown.",
        mockHookDescription:
          "What lies on the Earth-like planet? What unknown dangers will the investigation team face after landing?",
        mockEpisodeSummary:
          "The episode opens with a mission and an anomalous signal, quickly building suspense and setting up the investigation team's landing.",
      },
      nav: {
        all: "All",
        outline: "Outline",
        characters: "Characters",
        episodes: "Episode Summaries",
        episodeDetails: "Episode Details",
      },
      noEpisodeDetails: "No episode details yet",
      episode: "Episode {n}",
      storyOutline: "Story Outline",
      characters: "Characters",
      addCharacter: "Add Character",
      characterNamePlaceholder: "Enter character name",
      characterRolePlaceholder: "Enter character role",
      characterDescPlaceholder: "Enter character description",
      episodeSummary: "Episode Summaries",
      includedCharacters: "Characters:",
      addEpisodeCharacter: "Add Character",
      generationFailed: "Generation failed",
      detailGenerated: "Episode details generated",
      generating: "Generating...",
      regenerate: "Regenerate",
      generateEpisode: "Generate Episode Script",
      episodeDetail: {
        plot: "Detailed Episode Plot",
        plotHint: "Describe the episode story in detail in 300-500 words",
        cast: "Cast",
        scenes: "Scene List",
        script: "Detailed Script",
        scriptHint:
          "Includes scene divisions, shot descriptions, character actions, and dialogue with emotion tags",
        scriptPlaceholder: "Enter the detailed script",
        copy: "Copy",
        copySuccess: "Script text copied",
        copyFailed: "Failed to copy",
        highlights: "Highlight Suggestions",
        hotspot: "Highlight",
        hook: "Next-Episode Hook",
        summary: "Creation Summary",
        generationStarted: "Episode detail generation started",
        regenerationStarted: "Episode detail regeneration started",
        generateFailed: "Failed to generate episode details",
      },
      scriptTitleFallback: "Script Details",
      scriptGenre: "Script Genre",
      customValue: "Custom",
      defaultValue: "Default",
      episodeCountValue: "{n} episodes",
      durationValue: "{n} min",
      generatedHint: "The script is ready. You can review or edit its content.",
      saveScript: "Save Script",
      exporting: "Exporting...",
      exportTxt: "Export TXT",
    },
    generating: {
      scriptTitle: "AI is generating the script...",
      waitTip: "Estimated time: 3-5 minutes. Please wait.",
    },
    wizard: {
      next: "Next",
      generateOutline: "Generate outline {count}",
      regenerate: "Regenerate",
      generateScript: "Confirm and generate {count} episodes",
    },
    headers: {
      story: "Story Seed",
      genre: "Genre Setup",
      params: "Parameters",
    },
    footerHints: {
      story: "Your one-line story is the core inspiration for AI creation.",
      genre:
        "AI will assist creation based on your genre choices. You can customize them anytime.",
      params:
        "AI will adapt scene descriptions and pacing based on aspect ratio, style, and other settings.",
      outline:
        "AI generated an outline from your story. You can edit it directly.",
    },
    validation: {
      maxChars: "Input cannot exceed {count} characters",
    },
    status: {
      initializingEngine: "Initializing creation engine...",
      parsingGenre: "Parsing genre: {genre}...",
      buildingStory: "Building story structure...",
      generatingWorldview: "Generating world setting...",
      writingSummary: "Writing script summary...",
      almostDone: "Almost done...",
      organizingOutline: "Organizing outline...",
      outlineDone: "Outline generated",
      generatingOutline: "AI is generating the outline...",
      generatingCharacters: "Generating character settings...",
      writingEpisodes: "Writing episode outlines...",
      enrichingDetails: "Enriching story details...",
      scriptDone: "Script generated",
      organizingScript: "Organizing episode content...",
      viewingOutline: "Opening script outline...",
    },
    messages: {
      outlineSaveFailed: "Failed to save outline",
      genreListFailed: "Failed to load genres",
      subGenreListFailed: "Failed to load subgenres",
      keywordListFailed: "Failed to load keywords",
      pointsNotEnough: "Insufficient points",
      pointsCheckFailed: "Failed to check points",
      outlineTaskCreateFailed: "Failed to create outline generation task",
      outlineGenerateFailed: "Failed to generate outline",
      projectMissingRegenerate:
        "Script project ID is missing. Cannot regenerate.",
      outlineStatusFailed: "Failed to query outline generation status",
      outlineResultEmpty: "Generated outline is empty",
      outlineDetailFailed: "Failed to load outline details",
      cancelOutlineFailed: "Failed to cancel outline generation",
      projectMissingScript:
        "Script project ID is missing. Cannot generate script.",
      scriptTaskCreateFailed: "Failed to create episode generation task",
      scriptGenerateFailed: "Failed to generate episodes",
      scriptStatusFailed: "Failed to query episode generation status",
      aiUnavailable: "AI service is temporarily unavailable. Please try later.",
      cancelScriptFailed: "Failed to cancel episode generation",
      scriptDetailFailed: "Failed to load script details",
      scriptResultEmpty: "Generated episode result is empty",
      outlineConfirmed: "Script outline confirmed",
      featureComingSoon: "This feature is under development. Stay tuned.",
      scriptListFailed: "Failed to load script list",
      scriptIdMissingView: "Script ID is missing. Cannot view details.",
      noDownloadableScript: "No downloadable script for this record",
      deleteConfirm: "Delete this script? This action cannot be undone.",
      deleteTitle: "Confirm",
      confirmDelete: "Delete",
      scriptIdMissingDelete: "Script ID is missing. Cannot delete.",
      deleteSuccess: "Deleted successfully",
      deleteFailed: "Delete failed",
      downloadFailed: "Script download failed",
      scriptIdMissingExport: "Script ID is missing. Cannot export.",
    },
    presets: {
      genres: {
        romance: { name: "Romance" },
        suspense: { name: "Suspense" },
        fantasy: { name: "Fantasy" },
        scifi: { name: "Sci-Fi" },
        historical: { name: "Historical" },
        comedy: { name: "Comedy" },
        action: { name: "Action" },
        horror: { name: "Horror" },
        school: { name: "School" },
        career: { name: "Workplace" },
        custom: { name: "Custom" },
      },
      tags: {
        timeTravel: "Time Travel",
        urban: "Urban",
        childhoodSweethearts: "Childhood Friends",
        marriageFirst: "Marriage First",
        misunderstanding: "Misunderstanding",
        reunion: "Reunion",
        growth: "Growth",
        mutualCommitment: "Mutual Devotion",
        criminalInvestigation: "Crime Investigation",
        lockedRoom: "Locked Room",
        psychological: "Psychological",
        urbanLegend: "Urban Legend",
        truth: "Truth",
        twist: "Twist",
        evidence: "Evidence",
        tracking: "Tracking",
        isekai: "Isekai",
        magic: "Magic",
        cultivation: "Cultivation",
        superpower: "Superpower",
        adventure: "Adventure",
        awakening: "Awakening",
        destiny: "Destiny",
        secretRealm: "Secret Realm",
        interstellar: "Interstellar",
        ai: "AI",
        timeLoop: "Time Loop",
        apocalypse: "Apocalypse",
        civilization: "Civilization",
        exploration: "Exploration",
        crisis: "Crisis",
        restart: "Restart",
        palace: "Palace",
        martialWorld: "Martial World",
        powerStruggle: "Power Struggle",
        court: "Court",
        identity: "Identity",
        revenge: "Revenge",
        alliance: "Alliance",
        workplace: "Workplace",
        family: "Family",
        antiTrope: "Anti-trope",
        accidentalChaos: "Accidental Chaos",
        hilarious: "Hilarious",
        contrast: "Contrast",
        comeback: "Comeback",
        agent: "Agent",
        hotBlooded: "Hot-blooded",
        racing: "Racing",
        combat: "Combat",
        chase: "Chase",
        confrontation: "Confrontation",
        rescue: "Rescue",
        team: "Team",
        supernatural: "Supernatural",
        folklore: "Folklore",
        thriller: "Thriller",
        denseForest: "Dense Forest",
        curse: "Curse",
        taboo: "Taboo",
        escape: "Escape",
        unknown: "Unknown",
        youth: "Youth",
        club: "Club",
        graduation: "Graduation",
        friendship: "Friendship",
        dream: "Dream",
        crush: "Crush",
        regret: "Regret",
        startup: "Startup",
        businessWar: "Business War",
        legal: "Legal",
        medical: "Medical",
        competition: "Competition",
        choice: "Choice",
        breakthrough: "Breakthrough",
        partner: "Partner",
        originalGenre: "Original Genre",
        hybridGenre: "Hybrid Genre",
        freeCreation: "Free Creation",
        differentiation: "Differentiation",
      },
      styles: {
        seriousRealistic: "Serious Realism",
        lightComedy: "Light Comedy",
        mysteryHumor: "Mystery Humor",
        hotBlooded: "High-energy",
        healingWarm: "Warm Healing",
        darkOppressive: "Dark and Heavy",
        literaryFresh: "Fresh Literary",
        epicDrama: "Epic Drama",
        powerFantasy: "Power Fantasy",
      },
      videoStyles: {
        chinese3d: "3D Chinese Anime",
        japaneseAnime: "Japanese Anime",
        realistic: "Realistic",
        inkWash: "Ink Wash",
        pixel: "Pixel Art",
        cyberpunk: "Cyberpunk",
        minimalLine: "Minimal Lines",
      },
    },
  },
  projectLibrary: {
    personal: "Personal",
    groups: "Groups",
    groupsWithCount: "Groups ({count})",
    createGroup: "New Project Group",
    createGroupNoPermission:
      "Only Super Admins, Admins, and Sub-admins can create project groups",
    emptyNoGroups: "No project groups yet",
    emptyNoGroupsHint: "Ask an admin to create a group or invite you",
    members: "{count} members",
    inviteOpen: "Invite link on",
    inviteClosed: "Invite not enabled",
    inviteDisconnected: "Invite link disconnected",
    available: "Available {used} / {total}",
    quotaFixed: "Fixed quota · {n}/person",
    quotaGift: "Gift quota · {n}/person",
    quotaExclusive: "Exclusive quota · {n}/person",
    quotaUnlimited: "No quota",
    statusRunning: "In progress",
    statusPaused: "Paused",
    statusFilterPaused: "Paused",
    statusFinished: "Finished",
    statusClosed: "Closed",
    fetchListFailed: "Failed to load project groups",
    noMatch: "No matching project groups",
    tryAnotherKeyword: "Try a different keyword",
    create: {
      titleCanvas: "New Canvas Project Group",
      subtitle:
        "Complete project settings, member selection, and invitation in one step",
      close: "Close create project group",
      stepLabel: "Step {current}/3 · {name}",
      step1: "Basic info and quota",
      step1Desc: "Set the project name, credits, and shared member quota",
      step2: "Add school students",
      step3: "Invitation settings",
      name: "Project group name",
      namePlaceholder: "Enter a project group name",
      allocate: "Allocate project credits",
      allocatePlaceholder: "Enter credits to allocate",
      allocateHint: "Subscription credits are used first",
      creatorAvailable: "Creator available credits",
      quotaMode: "Member quota mode",
      quotaPerMember: "Quota per member",
      quotaPerMemberPlaceholder: "Enter quota per member",
      modeFixed: "Fixed quota · Same for all",
      modeGift: "Gift quota · Same for all",
      modeExclusive: "Exclusive quota · Same for all",
      modeUnlimited: "No quota · Shared project balance",
      step1Tip:
        "Credits are deducted from your available balance and can only be used in this {scene} project group. Member personal credits are not used automatically.",
      sceneCanvas: "Canvas",
      addMembers: "Add members",
      addMembersTip:
        "Select by organization in batch; quotas apply after creation",
      searchStudent: "Search school students",
      searchStudentPlaceholder: "Nickname or phone number",
      searchMemberPlaceholder: "Search name, phone, role, or account level",
      colName: "Name",
      colLevel: "Account level",
      colPhone: "Phone",
      colRole: "Role",
      allMembers: "All members ({count})",
      allMembersLabel: "All members",
      allMembersCount: "{count}",
      selectSubtree: "Select all",
      deselectSubtree: "Deselect all",
      selfLockedTip: "The creator is included by default and cannot be unchecked",
      step2Tip:
        "You can add any student in the school. Fixed quotas are applied in batch and cannot be edited per member.",
      generateLink: "Generate a join link after creation",
      linkExpiry: "Link validity",
      joinQuota: "Shared quota after joining",
      day1: "1 day",
      day7: "7 days",
      day30: "30 days",
      pointsUnit: "credits/person",
      step3Tip:
        "If remaining project credits are insufficient, new members can still join with an initial quota of 0. Admins can batch-set quotas after topping up.",
      prev: "Previous",
      next: "Next",
      submit: "Create project group",
      nameRequired: "Enter a project group name",
      allocateRequired: "Enter credits to allocate",
      allocateExceed: "Allocated credits cannot exceed the creator balance",
      allocateInvalid: "Allocated credits must be an integer greater than or equal to 0",
      quotaRequired: "Enter the quota per member",
      quotaInvalid: "Quota per member must be a positive integer",
      quotaExceed:
        "Quota per member cannot exceed the creator's available credits",
      success: "Project group created",
      failed: "Failed to create project group",
      noStudent: "No matching members",
      membersLoadFailed: "Failed to load member list",
      pointsLoadFailed: "Failed to load creator available credits",
      accountLevels: {
        admin: "Admin",
        subAdmin: "Sub-admin",
        member: "Member",
      },
    },
    workbench: {
      title: "Project Group Workbench",
      subtitle:
        "Centrally manage members, project credits, and work viewing permissions",
      backToList: "Back to project group list",
      searchPlaceholder: "Search project groups",
      createCanvas: "Create canvas",
      // Header refresh: reload stats cards and the current tab
      refresh: "Refresh",
      topup: "Adjust credits",
      manageInvite: "Manage invite link",
      creator: "Creator: {name} · {dept}",
      deptCanvas: "Canvas project group",
      quotaFixedShort: "Quota {n}/person",
      quotaGiftShort: "Gift {n}/person",
      quotaExclusiveShort: "Exclusive {n}/person",
      stats: {
        // Split-card left: remaining available
        available: "Remaining available credits",
        // Split-card right: project total
        total: "Project total credits",
        // Combined header card: remaining / allocated total
        availableTotal: "Remaining credits / Project total",
        unifiedQuota: "Unified quota",
        // Member mode: remaining quota after consumption
        personalAvailable: "Personal available quota",
        // Right side of the stats card: current member quota from currentMember.quotaLimit
        personalQuota: "Personal total quota",
        unifiedPersonal: "Unified quota / Personal total quota",
        members: "Project members",
        consumed: "Project cumulative consumption",
        // Right side of the stats card: current member usage from currentMember.usedIntegral
        personalConsumed: "Personal cumulative consumption",
        consumedPersonal: "Project cumulative consumption / Personal cumulative consumption",
        perPerson: "{n} / person",
        // Unified-quota column is the number only, without "/person"
        perPersonCompact: "{n}",
      },
      // Help icon label; tooltip stays hoverable when the action is disabled
      helpAria: "{label} info",
      tips: {
        available: "Remaining spendable credits in this project.",
        total: "Total credits allocated to the project.",
        availableTotal:
          "Remaining spendable credits in this project / Total credits allocated to the project.",
        unifiedQuota: "The member quota set uniformly for the project.",
        personalAvailable: "The remaining quota this member can still use in this project.",
        personalQuota: "The quota a single member actually received.",
        unifiedPersonal:
          "The former is the member quota set uniformly for the project; the latter is the quota a single member actually received.",
        members: "Number of members who have joined and have not been removed.",
        consumed: "Cumulative consumption by all project members.",
        personalConsumed: "A single member's cumulative consumption in this project.",
        consumedPersonal:
          "The former is cumulative consumption by all project members; the latter is a single member's cumulative consumption in this project.",
        topup:
          "Adjust the project group's total credits: you can add credits from the creator's available balance, or deduct unused project credits and return them to the creator; the adjusted project total cannot be lower than credits already consumed.",
        batchQuota:
          "Uniformly override the quota settings for all members of this project group. You can choose a fixed quota or no limit; with a fixed quota, all members receive the same total quota, and the consumed portion is not affected.",
        audit:
          "Records key operations such as project settings, member management, quota adjustments, and invite management.",
        status:
          "Controls whether the project is in progress, paused, finished, or closed; the status affects whether members can continue producing.",
        // Member-name search on the points ledger only matches consumption rows
        searchMemberName:
          "Search only supports credit details of the consumption type.",
      },
      tabs: {
        members: "Members & quota",
        works: "Member works",
        points: "Credit details",
        settings: "Project settings",
        // Creator audit log: ledger types 6–18
        audit: "Operation log",
        // Creator / member both have my works; members also have my credits / project info
        myWorks: "My works",
        myPoints: "My credit details",
        info: "Project info",
      },
      membersHint:
        "Manage member quotas and usage status. Project credits can only be used in this group.",
      addStudents: "Add member",
      batchQuota: "Batch set quota",
      columns: {
        member: "Member",
        role: "Role",
        consumedQuota: "Used / total quota",
        works: "Works",
        status: "Status",
        actions: "Actions",
        memberName: "Member name",
        time: "Time",
        action: "Action",
        // Points ledger: remark after Action
        operateDetail: "Action details",
        change: "Credit change",
        // Member "My credit details": change column as consumption
        consume: "Consumption",
        type: "Type",
        // Creator ledger: same wording as the remaining-credits stats card
        balance: "Remaining available credits",
        // Member ledger: same quota wording as the personal quota cards
        remainingQuota: "Remaining quota",
        model: "Model",
        resolution: "Resolution",
        quantity: "Duration / quantity",
        // Operation log: type / content / details
        operateTime: "Operation time",
        operator: "Operation object",
        operateContent: "Operation content",
        operateType: "Operation type",
        detail: "Details",
      },
      roleMember: "Member",
      roleCreator: "Creator",
      worksCount: "{n} works",
      statusNormal: "Normal",
      statusDisabled: "Disabled",
      adjustPoints: "Adjust quota",
      disable: "Disable",
      restore: "Restore",
      viewWorks: "View works",
      remove: "Remove",
      cannotRemoveCreator: "The creator cannot be removed",
      cannotDisableCreator: "The creator cannot be disabled",
      cannotAdjustCreator: "The creator quota cannot be adjusted",
      removeSuccess: "Member removed",
      removeFailed: "Failed to remove the member",
      removeConfirm: {
        title: "Remove {name}?",
        message:
          "This member will lose access to the project group. Used credits will not be refunded.",
        confirm: "Remove",
      },
      disableConfirm: {
        title: "Disable {name}?",
        message:
          "This member will not be able to continue producing in this project group.",
        confirm: "Disable",
      },
      restoreConfirm: {
        title: "Restore {name}?",
        message:
          "This member will be able to continue producing in this project group.",
        confirm: "Restore",
      },
      worksHint:
        "Creators can edit their own canvases; other members' works are view-only",
      myWorksHint: "Only works you created, which you can edit",
      memberWorksHint: "Only other members' works, view-only",
      filterAll: "All",
      filterEdit: "Editable",
      filterView: "Viewable",
      searchWork: "Search canvas/member name",
      searchWorkName: "Search work name",
      searchMemberName: "Search member name",
      allTypes: "All types",
      allTime: "All time",
      timeToday: "Today",
      timeWeek: "Last 7 days",
      timeMonth: "Last 30 days",
      startDate: "Start date",
      endDate: "End date",
      rangeSeparator: "to",
      datePickerFormat: "yyyy-MM-dd",
      workTimeAria: "Filter canvases by creation time",
      selectTimeRange: "Select time range",
      datePlaceholder: "Year / Month / Day",
      clearTimeRange: "Clear",
      incompleteTimeRange: "Select both start and end dates",
      startAfterEnd: "Start date cannot be after end date",
      typeCanvas: "Canvas work",
      typeStoryboard: "Storyboard work",
      typeImage: "Image work",
      createdAt: "Created {time}",
      workAuthorTime: "{name} · {time}",
      readOnlyView: "Viewable",
      enterEdit: "Editable",
      emptyWorks: "No member works yet",
      emptyMyWorks: "No works yet",
      emptyGroups: "No matching project groups",
      dash: "--",
      todayTime: "Today {time}",
      justNow: "Just now",
      settingsStatusTitle: "Project status",
      settingsStatusHint:
        "Set the current stage. Paused groups can be restored to in progress.",
      info: {
        groupName: "Project group name",
        status: "Project status",
        creator: "Creator",
        quotaMode: "Quota mode",
        hint: "Project credits can only be used in this project. You can edit your own works and view other members' works in read-only mode.",
      },
      statusRunningTitle: "In progress",
      statusRunningDesc: "Default status. Members can produce normally",
      statusPausedTitle: "Paused",
      statusPausedDesc: "Temporarily stop members from producing",
      statusFinishedTitle: "Finished",
      statusFinishedDesc: "Work is complete. Content can still be viewed",
      statusClosedTitle: "Close project",
      statusClosedDesc: "Stop production and end this project group",
      settingsSaved: "Project settings updated",
      statusChanged: "Project status switched to {status}",
      statusClosedSuccess: "Project closed",
      statusChangeFailed: "Failed to update project status",
      statusFinishedLocked:
        "This project group is already finished, so its status cannot be changed",
      statusFinishedActionLocked:
        "This project group is finished, so this action is unavailable",
      statusPausedActionLocked:
        "This project group is paused, so this action is unavailable",
      statusClosedLocked:
        "This project group is already closed, so its status cannot be changed",
      statusClosedActionLocked:
        "This project group is closed, so this action is unavailable",
      closeConfirm: {
        title: "Confirm closing the project",
        subtitle: "This will change the project group's production status",
        bodyLead: "Members will no longer be able to produce after it is closed.",
        bodyFollow:
          "Unused points will be returned to the creator's account according to official business rules. Please confirm whether to close this project.",
        confirm: "Confirm closing the project",
      },
      finishConfirm: {
        title: "Confirm marking as finished",
        subtitle: "This will change the project group's production status",
        body: "Once marked as finished, the project status can no longer be changed. Please confirm the work is complete.",
        confirm: "Confirm marking as finished",
      },
      disableSuccess: "Member disabled",
      restoreSuccess: "Member restored",
      disableFailed: "Failed to disable the member",
      restoreFailed: "Failed to restore the member",
      notFound: "Project group not found",
      fetchDetailFailed: "Failed to load project group details",
      // Member mode: blocked after disable / removal when opening a canvas or creating
      memberAccessBlocked: {
        disabledTitle: "Unable to continue",
        disabledMessage:
          "You have been disabled and can no longer produce in this project group",
        removedTitle: "Unable to continue",
        removedMessage:
          "You have been removed from this project group and can no longer access it",
        confirm: "Back to project group list",
      },
      fetchWorksFailed: "Failed to load canvases in this group",
      fetchLedgerFailed: "Failed to load credit details",
      emptyLedger: "No data",
      fetchAuditFailed: "Failed to load operation log",
      emptyAudit: "No data",
      // Operation log filter / type column groups
      audit: {
        settings: "Project settings",
        member: "Member management",
        quota: "Quota adjustment",
      },
      batch: {
        title: "Batch set member quota",
        subtitle: "This will overwrite settings for all {count} members",
        quotaMode: "Quota mode",
        quotaPerPerson: "Total quota per person",
        hint: "The cap cannot be lower than the highest credits already used by non-admin members. If a member has used 50 credits, a new cap of 200 leaves 150.",
        confirm: "Confirm batch setting",
        quotaBelowConsumed:
          "Quota per member cannot be lower than the highest credits already used by non-admin members ({n})",
        quotaExceed:
          "Quota per member cannot exceed creator credits plus project total credits",
        success: "Member quotas updated",
        failed: "Failed to batch-set member quotas",
      },
      topupDialog: {
        title: "Adjust project credits",
        desc: "Transfer credits from the creator's account, or return unused credits",
        opType: "Operation type",
        opAdd: "Add credits",
        opReduce: "Return credits",
        creatorAvailable: "Creator available credits",
        projectTotal: "Current project total",
        projectConsumed: "Project consumed credits",
        projectRemaining: "Current remaining credits",
        amount: "Adjustment amount",
        afterTotal: "Project total after adjustment",
        afterRemaining: "Remaining after adjustment",
        confirm: "Confirm adjustment",
        success: "Project credits topped up",
        successReduce: "Unused credits returned to the creator",
        failed: "Failed to allocate project credits",
        failedReduce: "Failed to return project credits",
        invalid: "Adjustment amount must be a positive integer",
        exceed: "Added credits cannot exceed the creator balance",
        exceedReduce: "Returned credits cannot exceed remaining project credits",
      },
      invite: {
        title: "Project group invite link",
        desc: "The link is valid and can be copied and shared",
        descExpired: "The link has expired. Regenerate it before sharing",
        descExpiryChanged: "Validity period changed. Regenerate the link before sharing",
        quota: "General quota",
        quotaValue: "{n} credits/person",
        expiry: "Validity period",
        hour24: "24 hours",
        hour72: "72 hours",
        link: "Invite link",
        hint: "The link inherits the group's unified quota mode. If project credits are insufficient, new members can still join.",
        preview: "Preview join process",
        copy: "Copy link",
        regenerateAndCopy: "Regenerate and copy",
        copied: "Invite link copied",
        regeneratedCopied: "New invite link generated and copied",
        copyFailed: "Copy failed",
        generateFailed: "Failed to generate invite link",
        loadFailed: "Failed to load invite link",
      },
      join: {
        title: "Invitation to join {name}",
        subtitle: "Member view. Click to join the project group",
        projectType: "Project type",
        joinQuota: "Quota after joining",
        quotaValue: "{n} credits",
        group: "Project group",
        permission: "Work permissions",
        permissionValue: "Own works editable, others read-only",
        pointsHint:
          "Project credits can only be used in this group. They cannot be used after leaving or switching projects.",
        confirm: "Confirm join",
        success: "Joined the project group",
      },
      invitePage: {
        badge: "Project invite",
        title: "You're invited to join a project group",
        summary:
          "{inviter} from {enterprise} invites you to join the project group “{project}”. Please confirm before {deadline}. The invite link will expire after that.",
        enterprise: "Enterprise",
        inviter: "Inviter",
        projectName: "Project group name",
        deadline: "Deadline",
        deadlineLocal: "{date} {time}",
        deadlineEn: "{time} on {date}",
        agreeHint:
          "After agreeing, you will become a member of this project group and use the quota assigned by the group.",
        agree: "Agree to join",
        enter: "Go to project group",
        invalid:
          "This invite link is invalid or expired. Ask the inviter for a new one.",
        alreadyMember:
          "You are already a member of this project group. You don't need to join again.",
        joinSuccess: "You have joined the project group",
        joinFailed: "Failed to join the project group. Please try again later.",
        loadFailed: "Failed to load the invitation. Please try again later.",
        empty: "—",
      },
      canvasDialog: {
        title: "Create project canvas",
        desc: "The canvas belongs to this group. Credits are consumed from the group quota only",
        name: "Canvas name",
        defaultName: "Untitled canvas",
        hint: "A blank canvas will be created. The creator can edit it; other members in the group have read-only access.",
        confirm: "Create canvas",
        success: "Canvas created",
        failed: "Failed to create canvas",
        nameRequired: "Enter a canvas name",
      },
      renameDialog: {
        title: "Rename project group",
        desc: "The new name will appear in the sidebar and workbench",
        name: "Project group name",
        namePlaceholder: "Enter a project group name",
        confirm: "Save name",
        success: "Project group name updated",
        failed: "Failed to rename the project group",
        nameRequired: "Enter a project group name",
        aria: "Rename project group",
      },
      addStudent: {
        title: "Add member",
        tip: "Batch-select by organization; quotas apply uniformly after adding",
        confirm: "Confirm add",
        success: "Members added to the project group",
        successWithRemove: "Member list updated",
        noneSelected: "Select members to add",
        failed: "Failed to add members",
        removeConfirm: {
          title: "Remove the unchecked members?",
          message:
            "{count} member(s) will lose access to the project group. Used credits will not be refunded.",
          confirm: "Remove",
        },
      },
      adjust: {
        title: "Adjust member quota",
        subtitle: "{name} · Only adjust the available quota in the current project group",
        member: "Member",
        current: "Used / total quota",
        newQuota: "New total quota",
        hint: "The total quota cannot be lower than this member's used credits. If a member has used 50 credits, a new cap of 200 leaves 150.",
        opType: "Operation type",
        opAdd: "Add quota",
        opReduce: "Reduce quota",
        amount: "Adjustment amount",
        consumed: "Currently consumed",
        currentTotal: "Current total quota",
        afterTotal: "Total quota after adjustment: {n} credits",
        confirm: "Confirm adjust",
        success: "Member quota updated",
        failed: "Failed to update member quota",
        invalid: "Total quota must be a non-negative integer",
        resultInvalid: "Total quota after adjustment cannot be negative",
        belowConsumed:
          "Total quota after adjustment cannot be less than the member's used quota",
        exceed:
          "The adjustment cannot exceed creator credits plus project total credits",
      },
      ledger: {
        transferIn: "Project credits transferred in",
        transferOut: "Project credits returned",
        canvasGenerate: "Canvas generation",
        storyboardGenerate: "Storyboard generation",
        credit: "Project credit",
        refund: "Project return",
        consume: "Member consumption",
        modelTumeng: "Tumeng 3.0",
        res2k: "2K / HD",
        quantityImages: "{n} images",
        durationSeconds: "{n}s",
      },
    },
  },
  visionCanvas: {
    title: "My Canvas",
    subtitle:
      "Project groups, members, and credits in this canvas are managed independently",
    createTime: "Created",
    searchPlaceholder: "Search project names",
    createProject: "New Project",
    loading: "Loading...",
    empty: "No canvas projects yet",
    emptyTip: "Create your first canvas project to get started",
    emptyNotFound: "No matching projects",
    emptyHint: "Try a different keyword",
    open: "Open",
    rename: "Rename",
    delete: "Delete",
    enterProject: "Enter Project",
    editMode: "Editable",
    viewMode: "View only",
    filterAll: "All",
    filterView: "Viewable",
    emptyFilterHint: "Try a different filter",
    createTimeLabel: "Created: {date}",
    creatorLabel: "Creator: {name}",
    untitled: "Untitled",
    dateFormat: "{year}-{month}-{day}",
    fetchListFailed: "Failed to load projects",
    renameSuccess: "Renamed successfully",
    operationFailed: "Operation failed",
    deleteConfirm: 'Delete project "{name}"?',
    deleteTitle: "Delete Project",
    cancel: "Cancel",
    deleteSuccess: "Deleted successfully",
    deleteFailed: "Failed to delete",
    pleaseLogin: "Please sign in first",
    loadFailed:
      "Failed to load Canvas. Make sure manshu-ui is running ({entry})",
  },
  statisticalDimension: {
    title: "Analytics",
    loading: "Loading data",
    search: "Search",
    clear: "Clear",
    apply: "Apply",
    exportTable: "Export",
    exporting: "Exporting...",
    exportSuccess: "Exported successfully",
    exportFailed: "Export failed",
    exportFailedRetry: "Export failed. Please try again later",
    exportFallback: "Export",
    unknown: "Unknown",
    none: "N/A",
    noData: "No data",
    pageInfo: "Page {page} / {total}",
    tipAria: "{label} info",
    serverError: "Internal server error. Please try again later",
    searchLabel: "Search:",
    filterLabel: "Filter:",
    projectLabel: "Project:",
    operatorLabel: "Operator:",
    points: "pts",
    pointsSpace: " pts",
    pointsPerSec: "pts/s",
    pointsPerSecValue: "{value} pts/s",
    pointsPerImage: "pts/image",
    times: "times",
    pieces: "",
    images: "",
    people: "{count} people",
    projectsCount: "{count} projects",
    segmentsCount: "{count} clips",
    timesPoints: "{count}× / {points} pts",
    imageVideoCount: "{image} imgs · {video} videos",
    countUnit: "{n}",
    wan: "{n}k",
    duration: {
      seconds: "{n}s",
      minutesSeconds: "{m}m {s}s",
      minutesOnly: "{m} min",
    },
    tabs: {
      overview: "Overview",
      project: "Workflow Projects",
      segment: "Drama Clips",
      canvas: "Canvas",
      member: "Members",
    },
    time: {
      range: "Time range",
      current: "Current:",
      compare: "Compare:",
      startDate: "Start date",
      endDate: "End date",
      selectStartDate: "Select start date",
      selectEndDate: "Select end date",
      custom: "Custom",
      month: "This month",
      lastMonth: "Last month",
      all: "All time",
      compareLastMonth: "Last month",
      compareBeforeLastMonth: "Month before last",
      previousPeriod: "previous period",
      startAfterEnd: "Start date cannot be after end date",
      yearMonth: "{year}-{month}",
      yearMonthFormat: "yyyy-MM",
    },
    filter: {
      canvasSearchAria: "Search canvas name or creator",
      canvasSearchPlaceholder: "Canvas name or creator...",
      projectSearchAria: "Search project name",
      projectSearchPlaceholder: "Search project name...",
      projectFilterAria: "Filter by project",
      projectFilterPlaceholder: "Project name...",
      operatorFilterAria: "Filter by operator",
      operatorFilterPlaceholder: "Operator...",
      memberFilterAria: "Filter member or project",
      memberFilterPlaceholder: "Member / project name...",
      memberProjectSearchAria: "Search member projects",
      canvasProjectSearchAria: "Search canvas projects",
      searchProjectPlaceholder: "Search projects...",
      clearProjectFilter: "Clear project filter",
    },
    overview: {
      totalConsumption: "Total points (Drama + Canvas)",
      dramaProject: "Drama projects",
      canvasProject: "Canvas projects",
      consumptionTrend: "Points consumption trend",
      pointsConsumption: "Points used",
      projectCount: "Projects",
      imageWasteRate: "Image waste rate",
      videoWasteRate: "Video waste rate",
      generateCount: "Generations",
    },
    stats: {
      totalPoints: "Total points used",
      totalDuration: "Total project duration",
      totalImages: "Total project images",
      avgPerSegment: "Avg points / clip",
      videoPerSec: "Video pts / sec",
      imagePerPiece: "Image pts / piece",
      tipDuration: "Effective duration of videos actually used",
      tipAvgSegment: "Total clip points ÷ clip count",
      tipVideoPerSec: "Points used ÷ effective duration",
      tipImagePerPiece: "Points used ÷ effective image count",
      comparedTo: "vs {period}",
    },
    operation: {
      video: "Video generation",
      image: "Image generation",
      script: "Script parsing",
    },
    asset: {
      character: "Characters",
      scene: "Scenes",
      prop: "Props",
      unit: "",
    },
    detail: {
      pointsTypeDistribution: "Points by type",
      videoStats: "Video stats",
      imageStats: "Image stats",
      collaboration: "Collaboration",
      costPoints: "Points used",
      segmentDuration: "Clip duration",
      budget: "Budget",
      diff: "Variance",
      remainingPrefix: "Left ",
      overspendPrefix: "Over ",
      remaining: "Left {value}",
      overspend: "Over {value}",
      avgPerSec: "Avg pts / sec",
      operator: "Operator",
      effectiveDuration: "Effective duration",
      actualGenerate: "Actually generated",
      wasteRate: "Waste rate",
      effectiveCount: "Effective count",
      effective: "Effective",
      actual: "Actual",
      cost: "Cost",
      video: "Video",
      image: "Image",
      text: "Text",
      total: "Total",
      costPointsValue: "{value} pts",
      imagesValue: "{value}",
    },
    project: {
      backToList: "‹ Back to projects",
      ranking: "Drama project ranking",
      consumptionTrend: "Points consumption trend",
      dataDistribution: "Distribution",
      operationDistribution: "By operation type",
      assetDistribution: "By asset",
      totalConsumption: "Total used",
      totalAssets: "Total assets",
      emptyTitle: "No matching projects",
      emptyDesc: "Try adjusting your search",
      emptyCollaborators: "No collaboration data",
      tipImageCount: "Number of effective images actually used",
      tipVideoDuration:
        "Video duration is the effective length used from generated videos",
      columns: {
        name: "Project",
        scriptNum: "Episodes",
        scriptDuration: "Ep. duration",
        segments: "Clips",
        members: "Members",
        duration: "Video duration",
        imageCount: "Images",
        total: "Points used",
        budget: "Budget",
        diff: "Variance",
        progress: "Progress",
      },
    },
    segment: {
      title: "Drama clips",
      backToList: "‹ Back to clips",
      emptyTitle: "No matching clips",
      emptyDesc: "Try adjusting your filters",
      ofTotal: "{percent}% of total",
      missingScriptId: "This clip has no scriptId; details unavailable",
      detailFailed: "Failed to load clip details. Please try again later",
      columns: {
        name: "Clip",
        project: "Project",
        scriptDuration: "Episode duration",
        duration: "Clip duration",
        cost: "Points used",
        perSecond: "Pts / sec",
        operator: "Operator",
      },
    },
    canvas: {
      listTitle: "Canvas projects",
      emptyTitle: "No canvas projects",
      emptyDesc: "Try adjusting your search",
      columns: {
        name: "Canvas",
        creator: "Creator",
        imageGenerate: "Image gen",
        videoGenerate: "Video gen",
        lastOperateTime: "Last activity",
      },
    },
    member: {
      title: "Member details",
      emptyTitle: "No matching members",
      emptyDesc: "Try adjusting your search",
      backToList: "‹ Back to members",
      participateSummary: "{projects} projects · {segments} clips completed",
      projectConsumption: "Usage by project",
      dramaProject: "Drama projects",
      canvasProject: "Canvas projects",
      costAllProjects: "Points used (all projects)",
      avgPerSec: "Avg pts / sec",
      costPerImage: "Pts / image",
      efficiency: "Efficiency",
      efficiencyNormal: "Normal",
      operationRatio: "By operation type",
      dramaProjects: "Drama projects",
      canvasProjects: "Canvas projects",
      noMatchProject: "No matching projects",
      noProject: "No project",
      dailyTrend: "Daily usage — {project} · {period}",
      generateTrend: "Generation usage — {project} · {period}",
      segmentTitle: "Drama clips — {project}",
      generateDetail: "Generation details",
      imageGen: "Image generation",
      videoGen: "Video generation",
      columns: {
        name: "Member",
        projects: "Projects",
        used: "Used",
        remaining: "Remaining",
        allocated: "Allocated",
        progress: "Progress",
        segment: "Clip",
        script: "Script parsing",
        image: "Image generation",
        imageCount: "Images",
        video: "Video generation",
        videoDuration: "Video duration",
        total: "Total",
        canvasName: "Canvas",
        creator: "Creator",
      },
    },
    export: {
      projectFile: "drama_projects_{label}.xlsx",
      segmentFile: "drama_clips_{label}.xlsx",
      canvasFile: "canvas_projects_{label}.xlsx",
      memberFile: "members.csv",
    },
  },
};
